import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { signPredictAccountMessage } from './predictAuth';

// 部署到 Cloudflare Pages 時可設 VITE_API_ORIGIN 為 Worker 完整網址；本地留空則走 proxy
// 若在 pages.dev 上且未設或設錯，自動用 predict-api（避免打到 predictfun.workers.dev 導致 CORS）
const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const isPredict2Site = import.meta.env.VITE_SITE_ID === 'predict2' || (typeof window !== 'undefined' && /predict2-web\.pages\.dev$/i.test(window.location?.hostname || ''));
const API_BASE = envOrigin || (isPages ? 'https://predict-api.predictfun.workers.dev' : '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : '/' + path) : path);

const STORAGE_API_KEY = 'predict_api_key';
const STORAGE_ADDRESS = 'predict_address';
const STORAGE_POLL = 'predict_poll_interval';
const STORAGE_POLY_POLL = 'predict_poly_poll_interval';
const STORAGE_ACCOUNTS = 'predict_accounts';

const POLL_OPTIONS = [
  { value: 0, label: '不輪詢' },
  { value: 3, label: '3 秒' },
  { value: 5, label: '5 秒' },
  { value: 10, label: '10 秒' },
  { value: 30, label: '30 秒' },
  { value: 60, label: '60 秒' },
  { value: 120, label: '120 秒' },
];

const POLY_POLL_OPTIONS = [
  { value: 5, label: '5 秒' },
  { value: 10, label: '10 秒' },
  { value: 15, label: '15 秒' },
  { value: 30, label: '30 秒' },
];

// 測試機器人按鈕
function TestBotButton() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const handleTest = async () => {
    setLoading(true);
    setResult(null);
    try {
      const resp = await fetch(api('/api/bot/test'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.success) {
        const parts = [];
        if (data.telegram === true) parts.push('Telegram 已送達');
        if (data.telegram === false) parts.push('Telegram 發送失敗');
        if (data.wecom === true) parts.push('企業微信已送達');
        if (data.wecom === false) parts.push('企業微信發送失敗');
        setResult({ ok: true, msg: (parts.length ? parts.join('；') : '') || data.message });
      } else {
        setResult({ ok: false, msg: data?.message || '發送失敗' });
      }
    } catch (e) {
      setResult({ ok: false, msg: e?.message || '網路錯誤' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button type="button" className="positions-query-btn" style={{ background: '#334155' }} onClick={handleTest} disabled={loading}>
        {loading ? '發送中…' : '測試機器人'}
      </button>
      {result && (
        <span style={{ fontSize: 13, color: result.ok ? '#22c55e' : '#ef4444' }} title={result.msg}>
          {result.ok ? '✓ ' + result.msg : '✗ ' + result.msg}
        </span>
      )}
    </div>
  );
}

// 生成唯一 ID
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// 多帳戶管理組件
function AccountManager({ accounts, currentAddress, onSelect, onAdd, onUpdate, onDelete }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newPrivateKey, setNewPrivateKey] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editPrivateKey, setEditPrivateKey] = useState('');

  const handleAdd = () => {
    const addr = newAddress.trim();
    if (!addr) return;
    onAdd({
      address: addr,
      note: newNote.trim(),
      privateKey: newPrivateKey.trim() || undefined,
      telegramEnabled: true,
    });
    setNewAddress('');
    setNewNote('');
    setNewPrivateKey('');
    setShowAddForm(false);
  };

  const handleStartEdit = (acc) => {
    setEditingId(acc.id);
    setEditNote(acc.note || '');
    setEditPrivateKey('');
  };

  const handleSaveEdit = (acc) => {
    const updates = { note: editNote.trim() };
    if (editPrivateKey.trim()) updates.privateKey = editPrivateKey.trim();
    onUpdate(acc.id, updates);
    setEditingId(null);
    setEditNote('');
    setEditPrivateKey('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNote('');
    setEditPrivateKey('');
  };

  return (
    <div className="account-manager">
      <div className="account-manager-header">
        <span className="account-manager-title">已保存帳戶</span>
        <button
          type="button"
          className="account-add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? '取消' : '+ 添加帳戶'}
        </button>
      </div>

      {showAddForm && (
        <div className="account-add-form">
          <input
            type="text"
            className="account-input"
            placeholder="存款帳戶地址 0x..."
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
          />
          <input
            type="text"
            className="account-input account-input-note"
            placeholder="備註（可選）"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <input
            type="password"
            className="account-input account-input-key"
            placeholder="Privy 私鑰（可選，用於掛單 JWT）"
            value={newPrivateKey}
            onChange={(e) => setNewPrivateKey(e.target.value)}
          />
          <button
            type="button"
            className="account-save-btn"
            onClick={handleAdd}
            disabled={!newAddress.trim()}
          >
            保存
          </button>
        </div>
      )}

      <div className="account-list">
        {accounts.length === 0 ? (
          <div className="account-list-empty">尚無保存的帳戶</div>
        ) : (
          accounts.map((acc) => {
            const isActive = acc.address.toLowerCase() === currentAddress?.toLowerCase();
            const isEditing = editingId === acc.id;
            return (
              <div
                key={acc.id}
                className={`account-item ${isActive ? 'account-item-active' : ''}`}
              >
                <div className="account-item-main" onClick={() => onSelect(acc)}>
                  <div className="account-item-address" title={acc.address}>
                    {acc.address.slice(0, 6)}…{acc.address.slice(-4)}
                  </div>
                  {isEditing ? (
                    <div className="account-edit-fields" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        className="account-edit-input"
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        placeholder="備註"
                      />
                      <input
                        type="password"
                        className="account-edit-input account-edit-key"
                        value={editPrivateKey}
                        onChange={(e) => setEditPrivateKey(e.target.value)}
                        placeholder="留空不變，填寫則更新 Privy 私鑰"
                      />
                    </div>
                  ) : (
                    <div className="account-item-meta">
                      <span className="account-item-note">{acc.note || '—'}</span>
                      {acc.privateKey && (
                        <span className="account-item-key-badge" title="已設定掛單 JWT 密鑰">
                          🔑
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="account-item-actions">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        className="account-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleSaveEdit(acc); }}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="account-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                  <label
                    className="account-alert-toggle"
                    title="是否啟用 Telegram 機器人提醒"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={acc.telegramEnabled !== false}
                      onChange={(e) => {
                        onUpdate(acc.id, { telegramEnabled: e.target.checked });
                      }}
                    />
                    <span>提醒</span>
                  </label>
                      <button
                        type="button"
                        className="account-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleStartEdit(acc); }}
                        title="編輯備註"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="account-action-btn account-action-delete"
                        onClick={(e) => { e.stopPropagation(); onDelete(acc.id); }}
                        title="刪除帳戶"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function getOutcomeName(tokenId, outcomes) {
  if (!Array.isArray(outcomes)) return '—';
  const o = outcomes.find((x) => String(x?.onChainId) === String(tokenId));
  return o?.name ?? '—';
}

// 從 Polymarket 體育連結解析兩選項（如 nba-tor-mil-2026-02-22 -> [TOR, MIL]），用於強制切換按鈕
function parsePolyMarketOptions(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.trim().match(/nba-([a-z]{2,5})-([a-z]{2,5})(?:-\d|$)/i);
  if (m) return [m[1].toUpperCase(), m[2].toUpperCase()];
  const m2 = url.trim().match(/(?:sports\/)?(?:nhl|nfl|epl|mlb)-([a-z]{2,5})-([a-z]{2,5})(?:-\d|$)/i);
  if (m2) return [m2[1].toUpperCase(), m2[2].toUpperCase()];
  return null;
}

/** 從 Polymarket 體育連結提取開賽日期，回傳 YYYY-MM-DDTHH:mm 格式，預設 20:00；無則回傳 null */
function parseStartTimeFromPolyUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.trim().match(/(\d{4})-(\d{2})-(\d{2})(?:\?|$|\/)/);
  if (!m) return null;
  const [, y, mon, d] = m;
  return `${y}-${mon}-${d}T20:00`;
}

// 單一市場下方的 Polymarket 盤口：有 polyOrderbookItem 時顯示卖3/卖2/卖1、买1/买2/买3（從大到小豎排）；否則顯示買一賣一
function PolymarketDepthInline({ polyData, polyOrderbookItem, loading, orderList, market }) {
  // Hooks 必須在頂層呼叫，不可放在條件或 return 之後
  const orderSideOutcomes = useMemo(() => {
    const set = new Set();
    (orderList || []).forEach((item) => {
      const order = item.order || {};
      const side = order.side === 0 ? 'Buy' : 'Sell';
      const outcomeName = getOutcomeName(order.tokenId, market?.outcomes);
      const outcome = String(outcomeName).toLowerCase() === 'yes' ? 'Yes' : 'No';
      set.add(`${side}_${outcome}`);
    });
    return [...set];
  }, [orderList, market?.outcomes]);

  // 若已有完整 orderbook，就直接顯示，不因全局 loading 反覆切換「載入中」，避免畫面跳動
  const polyAsks = (polyOrderbookItem?.asks || []).slice().sort((a, b) => (a[0] || 0) - (b[0] || 0)).slice(0, 3);
  const polyBids = (polyOrderbookItem?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0)).slice(0, 3);
  const hasFullBook = polyAsks.length > 0 || polyBids.length > 0;
  if (loading && !hasFullBook) {
    return (
      <div className="polymarket-inline polymarket-inline-loading">
        Polymarket 買一賣一：載入中…
      </div>
    );
  }
  // Poly 賣單市場：卖一卖二卖三（價格由低到高，卖一=最佳賣價）
  if (hasFullBook) {
    return (
      <div className="polymarket-inline">
        <div className="polymarket-inline-title">Poly 市場（依掛單 outcome）</div>
        <div className="polymarket-inline-orderbook">
          <table className="orders-table polymarket-inline-table">
            <thead>
              <tr><th>賣</th><th>價格</th><th>數量</th><th>總計</th></tr>
            </thead>
            <tbody>
              {polyAsks.map(([p, s], i) => (
                <tr key={`a${i}`} className="boost-ask-row">
                  <td>賣{i + 1}</td>
                  <td>{(p * 100).toFixed(0)}¢</td>
                  <td>{s.toFixed(0)}</td>
                  <td>${(p * s).toFixed(2)}</td>
                </tr>
              ))}
              {polyAsks.length === 0 && <tr><td colSpan={4} className="boost-empty">—</td></tr>}
            </tbody>
          </table>
          <table className="orders-table polymarket-inline-table">
            <thead>
              <tr><th>買</th><th>價格</th><th>數量</th><th>總計</th></tr>
            </thead>
            <tbody>
              {polyBids.map(([p, s], i) => (
                <tr key={`b${i}`} className="boost-bid-row">
                  <td>買{i + 1}</td>
                  <td>{(p * 100).toFixed(0)}¢</td>
                  <td>{s.toFixed(0)}</td>
                  <td>${(p * s).toFixed(2)}</td>
                </tr>
              ))}
              {polyBids.length === 0 && <tr><td colSpan={4} className="boost-empty">—</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (!polyData) return null;

  const rowConfig = [
    { key: 'Buy_Yes', side: 'Buy', outcome: 'Yes', label: '买一', data: polyData.buyYes },
    { key: 'Buy_No', side: 'Buy', outcome: 'No', label: '买一', data: polyData.buyNo },
    { key: 'Sell_Yes', side: 'Sell', outcome: 'Yes', label: '卖一', data: polyData.sellYes },
    { key: 'Sell_No', side: 'Sell', outcome: 'No', label: '卖一', data: polyData.sellNo },
  ];
  const rowsToShow = rowConfig.filter((r) => orderSideOutcomes.includes(r.key) && r.data != null);
  if (rowsToShow.length === 0) return null;
  return (
    <div className="polymarket-inline">
      <div className="polymarket-inline-title">Polymarket 買一賣一</div>
      <table className="orders-table polymarket-inline-table">
        <thead>
          <tr>
            <th>Side</th>
            <th>Outcome</th>
            <th />
            <th>Price</th>
            <th>Shares</th>
          </tr>
        </thead>
        <tbody>
          {rowsToShow.map((row) => {
            const d = row.data;
            const isYes = row.outcome.toLowerCase() === 'yes';
            const priceStr = d != null && d.price != null
              ? `${(d.price * 100).toFixed(2)}¢`
              : '—';
            const sharesStr = d != null && d.shares > 0
              ? d.shares >= 1 ? d.shares.toFixed(2) : d.shares.toString()
              : '—';
            return (
              <tr key={row.key}>
                <td className="orders-cell-side">{row.side}</td>
                <td className={`orders-cell-outcome ${isYes ? 'outcome-yes' : 'outcome-no'}`}>
                  {row.outcome}
                </td>
                <td className="orders-cell-label">{row.label}</td>
                <td className="orders-cell-price">{priceStr}</td>
                <td className="orders-cell-shares">{sharesStr}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getOrderKeyForStatus(item, index) {
  if (!item) return null;
  const order = item.order || {};
  const marketId = item.marketId ?? 'unknown';
  const tokenId = order.tokenId ?? '0';
  return item.id || order.hash || `${marketId}-${tokenId}-${index}`;
}

function OpenOrdersList({ orders, markets, polymarketByQuestion, polymarketLoading, polymarketLinks, polyOrderbookByMarket, marketStartTimes, cancelBeforeStartByMarket, polyOutcomes, polyOutcomeOverrides, onSetPolyOverride, polyOptionsFromApi, polyViewMode, onSetPolyViewMode, onSavePolyLink, onDeletePolyLink, onSaveStartTime, onToggleCancelBeforeStart, onSavePolyOutcome, onReplaceAtBest, replaceOrderId, onRemoveOrder, removingOrderId, onAddToAutoTrading, polyAutoOptions, priceRuleEnabledByMarket, onTogglePriceRule }) {
  const byMarket = useMemo(() => {
    const map = {};
    orders.forEach((item, idx) => {
      const id = item.marketId ?? 'unknown';
      const withIndex = { ...item, _orderIndex: idx };
      if (!map[id]) map[id] = [];
      map[id].push(withIndex);
    });
    return map;
  }, [orders]);

  const marketIds = useMemo(() => Object.keys(byMarket), [byMarket]);
  const [linkDrafts, setLinkDrafts] = useState({});
  const [startTimeDrafts, setStartTimeDrafts] = useState({});
  const [polyOutcomeDrafts, setPolyOutcomeDrafts] = useState({});
  const [savedMarketId, setSavedMarketId] = useState(null);
  const autoCancelHandledRef = useRef(new Set());
  const statusDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const [statusPos, setStatusPos] = useState({ x: null, y: null });
  const orderRowRefs = useRef({});
  const prevOrderKeysRef = useRef(new Set());
  const highlightTimeoutsRef = useRef({});
  const [highlightedOrderKeys, setHighlightedOrderKeys] = useState({});
  const {
    autoCancelDanger = false,
    autoCancelBalancedLowBook = false,
    balancedLowBookMinShares = 3000,
    autoCancelOnPartialFill = false,
    autoCancelBuyOneBelowOrder2x = false,
    cancelAllOnPartialFillOver = 0,
    priceSumThresholdCents = 0,
    priceSpreadThresholdCents = 0,
  } = polyAutoOptions || {};
  const partialFillHandledRef = useRef(new Set());
  const cancelAllOverHandledRef = useRef(new Set()); // 已觸發「單筆成交超過門檻→撤銷該市場全部」的 marketId
  const cancelBeforeStartHandledRef = useRef(new Set()); // 已觸發「開賽前 1 小時撤銷該市場全部」的 marketId
  const [startCheckTick, setStartCheckTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStartCheckTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // 新掛單高亮：偵測新出現的訂單，10 秒內加上綠色背景
  useEffect(() => {
    const prevKeys = prevOrderKeysRef.current;
    const nextKeys = new Set();
    const newly = [];

    orders.forEach((item, idx) => {
      const key = getOrderKeyForStatus(item, idx);
      if (!key) return;
      nextKeys.add(key);
      if (!prevKeys.has(key)) newly.push(key);
    });

    prevOrderKeysRef.current = nextKeys;

    newly.forEach((key) => {
      setHighlightedOrderKeys((prev) => ({ ...prev, [key]: true }));
      if (highlightTimeoutsRef.current[key]) {
        clearTimeout(highlightTimeoutsRef.current[key]);
      }
      highlightTimeoutsRef.current[key] = setTimeout(() => {
        setHighlightedOrderKeys((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        delete highlightTimeoutsRef.current[key];
      }, 10000);
    });
  }, [orders]);

  // 卸載時清除計時器
  useEffect(() => () => {
    Object.values(highlightTimeoutsRef.current).forEach((t) => clearTimeout(t));
  }, []);

  // 懸浮窗：逐筆掛單的狀態列 + 是否在買一（依 orders 原始順序編號）
  const statusSummary = useMemo(() => {
    const items = [];
    const epsPrice = 0.0001;
    const epsBest = 0.001;
    const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;

    orders.forEach((item, idx) => {
      const market = markets[item.marketId] || {};
      const order = item.order || {};
      const tokenId = order.tokenId;

      // Predict 買一（依 outcome 對應的 orderbookByToken，否則退回整體 YES 盤口）
      const obByToken = market.orderbookByToken || {};
      const predictOb =
        tokenId && obByToken[tokenId]
          ? obByToken[tokenId]
          : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
      const pBids = predictOb.bidsTop3 || [];
      const pBuyOne = pBids[0];
      const pBuyPrice = pBuyOne != null ? pBuyOne[0] : null;

      // Polymarket 買一（多選項市場需依 outcome 如 Nuggets→DEN 取對應子市場）
      const outcomeName = tokenId != null ? (getOutcomeName(tokenId, market.outcomes) || '').trim() : '';
      const polyOb = (outcomeName && polyOrderbookByMarket?.[`${item.marketId}::${outcomeName}`]) || polyOrderbookByMarket?.[item.marketId];
      const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
      const polyBuyOne = polyBids[0];
      const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;

      let riskStatus = null;
      if (pBuyPrice != null && polyBuyPrice != null) {
        const diff = Math.abs(polyBuyPrice - pBuyPrice);
        if (spreadThreshold > 0 && diff <= spreadThreshold) riskStatus = 'safe';
        else if (polyBuyPrice - pBuyPrice > epsPrice) riskStatus = 'safe';
        else if (pBuyPrice - polyBuyPrice > epsPrice) riskStatus = 'danger';
        else riskStatus = 'balanced';
      }

      // 該筆訂單是否在買一（買單對比 bestBid、賣單對比 bestAsk）
      const tokenPrices = market.bestPricesByToken?.[tokenId] || {};
      const { bestBid, bestAsk } = tokenPrices;
      const isBuy = order.side === 0;
      const makerAmt = Number(order.makerAmount) || 0;
      const takerAmt = Number(order.takerAmount) || 0;
      const priceDecimal =
        isBuy
          ? takerAmt > 0 ? makerAmt / takerAmt : null
          : makerAmt > 0 ? takerAmt / makerAmt : null;
      let isAtBest = false;
      if (priceDecimal != null) {
        if (isBuy && bestBid != null && Math.abs(priceDecimal - bestBid) <= epsBest) isAtBest = true;
        if (!isBuy && bestAsk != null && Math.abs(priceDecimal - bestAsk) <= epsBest) isAtBest = true;
      }

      let statusText = '狀態未知';
      if (riskStatus === 'danger') statusText = '危險';
      else if (riskStatus === 'balanced') statusText = '均衡';
      else if (riskStatus === 'safe') statusText = '安全';

      const atBestText = isAtBest ? '' : '（該訂單不在買一）';
      const key = getOrderKeyForStatus(item, idx);

      items.push({
        key: key || item.id || `${item.marketId}-${tokenId}-${idx}`,
        orderKey: key,
        riskStatus,
        text: `${idx + 1}、${statusText}${atBestText}`,
      });
    });

    return items;
  }, [orders, markets, polyOrderbookByMarket, priceSpreadThresholdCents]);

  // 初始化懸浮窗位置（視窗右下角），並處理拖曳
  useEffect(() => {
    if (statusPos.x == null && typeof window !== 'undefined') {
      const margin = 24;
      const width = 260;
      const height = 140;
      setStatusPos({
        x: window.innerWidth - width - margin,
        y: window.innerHeight - height - margin,
      });
    }
  }, [statusPos.x, statusPos.y]);

  useEffect(() => {
    function handleMove(e) {
      if (!statusDragRef.current.dragging) return;
      const margin = 8;
      const maxX = (window.innerWidth || 0) - margin;
      const maxY = (window.innerHeight || 0) - margin;
      const nextX = e.clientX - statusDragRef.current.offsetX;
      const nextY = e.clientY - statusDragRef.current.offsetY;
      setStatusPos({
        x: Math.min(maxX, Math.max(margin, nextX)),
        y: Math.min(maxY, Math.max(margin, nextY)),
      });
    }
    function handleUp() {
      if (statusDragRef.current.dragging) {
        statusDragRef.current.dragging = false;
      }
    }
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []);

  const handleStatusMouseDown = useCallback((e) => {
    // 只處理左鍵拖曳
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    statusDragRef.current = {
      dragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  }, []);

  const scrollToOrderByKey = useCallback((orderKey, align = 'center') => {
    if (!orderKey) return;
    const el = orderRowRefs.current?.[orderKey];
    if (el && typeof el.scrollIntoView === 'function') {
      const block = align === 'start' || align === 'end' ? align : 'center';
      el.scrollIntoView({ behavior: 'smooth', block });
    }
  }, []);

  const scrollToOrderIndex = useCallback((targetIndex, align = 'center') => {
    if (!orders || orders.length === 0) return;
    const safeIndex = Math.min(orders.length - 1, Math.max(0, targetIndex));
    const targetItem = orders[safeIndex];
    if (!targetItem) return;
    const key = getOrderKeyForStatus(targetItem, safeIndex);
    scrollToOrderByKey(key, align);
  }, [orders, scrollToOrderByKey]);

  const handleStatusItemClick = useCallback((s) => {
    if (!s?.orderKey) return;
    scrollToOrderByKey(s.orderKey, 'center');
  }, [scrollToOrderByKey]);

  // 監控訂單：有部分成交時立即撤掉該筆剩餘掛單（每筆只觸發一次）
  useEffect(() => {
    if (!autoCancelOnPartialFill || !onRemoveOrder) return;
    const handled = partialFillHandledRef.current;
    orders.forEach((item) => {
      const id = item?.id;
      if (!id || handled.has(id)) return;
      const filled = Number(item.amountFilled) || 0;
      if (filled <= 0) return;
      handled.add(id);
      onRemoveOrder(item);
    });
  }, [autoCancelOnPartialFill, orders, onRemoveOrder]);

  // 當單筆訂單成交 shares 超過門檻時，撤銷該市場全部訂單（每個市場只觸發一次）
  useEffect(() => {
    const threshold = Math.max(0, Math.floor(Number(cancelAllOnPartialFillOver) || 0));
    if (threshold <= 0 || !onRemoveOrder) return;
    const handled = cancelAllOverHandledRef.current;
    marketIds.forEach((marketId) => {
      if (handled.has(marketId)) return;
      const list = byMarket[marketId] || [];
      const over = list.some((item) => {
        const order = item.order || {};
        const isBuy = order.side === 0; // 只對買單生效，賣單不觸發此規則
        if (!isBuy) return false;
        const raw = Number(item.amountFilled) || 0;
        // Predict API 的 shares 採用 wei（1e18），小於 1e10 視為已是「股數」單位
        const filledShares = raw >= 1e10 ? raw / 1e18 : raw;
        return filledShares > threshold;
      });
      if (!over) return;
      handled.add(marketId);
      list.forEach((item) => {
        if (item?.id) onRemoveOrder(item);
      });
    });
  }, [cancelAllOnPartialFillOver, marketIds, byMarket, onRemoveOrder]);

  // 當 Poly 買一價格（¢）+ 我掛單價格（¢）超過門檻時，自動撤掉該筆 Buy 單（每筆只觸發一次）
  const priceSumHandledRef = useRef(new Set());
  useEffect(() => {
    const threshold = Math.max(0, Math.floor(Number(priceSumThresholdCents) || 0));
    if (threshold <= 0 || !onRemoveOrder) return;
    const handled = priceSumHandledRef.current;

    marketIds.forEach((marketId) => {
      if (!priceRuleEnabledByMarket?.[marketId]) return; // 僅對已啟用此規則的市場生效
      const list = byMarket[marketId] || [];
      if (!list.length) return;
      const market = markets[marketId] || {};
      const fo = list[0];
      const foOutcome = fo?.order?.tokenId != null ? (getOutcomeName(fo.order.tokenId, market.outcomes) || '').trim() : '';
      const polyOb = (foOutcome && polyOrderbookByMarket?.[`${marketId}::${foOutcome}`]) || polyOrderbookByMarket?.[marketId];
      const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
      const polyBuyOne = polyBids[0];
      const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;
      if (polyBuyPrice == null || polyBuyPrice <= 0 || polyBuyPrice >= 1) return;
      const polyBuyCents = Math.round(polyBuyPrice * 100);

      list.forEach((item) => {
        const id = item?.id;
        if (!id || handled.has(id)) return;
        const order = item.order || {};
        if (order.side !== 0) return; // 只對 Buy 單生效
        const makerAmt = Number(order.makerAmount) || 0;
        const takerAmt = Number(order.takerAmount) || 0;
        if (takerAmt <= 0) return;
        const priceDecimal = makerAmt / takerAmt;
        if (!Number.isFinite(priceDecimal) || priceDecimal <= 0 || priceDecimal >= 1) return;
        const orderCents = Math.round(priceDecimal * 100);
        if (polyBuyCents + orderCents > threshold) {
          handled.add(id);
          onRemoveOrder(item);
        }
      });
    });
  }, [priceSumThresholdCents, marketIds, byMarket, markets, polyOrderbookByMarket, onRemoveOrder, priceRuleEnabledByMarket]);

  // 開賽前 1 小時撤銷該市場全部訂單（僅對已勾選「開賽前 1 小時自動撤單」的市場，依自訂開賽時間，每分鐘檢查，每個市場只觸發一次）
  useEffect(() => {
    if (!onRemoveOrder || !marketStartTimes || typeof marketStartTimes !== 'object' || !cancelBeforeStartByMarket) return;
    const ONE_HOUR_MS = 60 * 60 * 1000;
    const handled = cancelBeforeStartHandledRef.current;
    const now = Date.now();
    marketIds.forEach((marketId) => {
      if (!cancelBeforeStartByMarket[marketId]) return;
      const startStr = marketStartTimes[marketId];
      if (!startStr || handled.has(marketId)) return;
      const startMs = new Date(startStr.trim()).getTime();
      if (Number.isNaN(startMs)) return;
      if (now < startMs - ONE_HOUR_MS) return;
      handled.add(marketId);
      const list = byMarket[marketId] || [];
      list.forEach((item) => {
        if (item?.id) onRemoveOrder(item);
      });
    });
  }, [marketIds, byMarket, marketStartTimes, cancelBeforeStartByMarket, onRemoveOrder, startCheckTick]);

  // 依 Polymarket 比較結果自動撤單（危險 / 均衡且買一 shares 過低 / Poly 買一不足掛單 2 倍）
  useEffect(() => {
    if ((!autoCancelDanger && !autoCancelBalancedLowBook && !autoCancelBuyOneBelowOrder2x) || !onRemoveOrder) return;
    const handled = autoCancelHandledRef.current;

    marketIds.forEach((marketId) => {
      const market = markets[marketId] || {};
      const list = byMarket[marketId] || [];
      if (!list || list.length === 0) return;

      // 以該市場當前第一筆掛單的 outcome 作為基準（與下方「Predict / Polymarket 買一／賣一」區塊一致）
      const firstOrder = list[0];
      const tokenId = firstOrder?.order?.tokenId;
      const outcomeName = tokenId != null ? (getOutcomeName(tokenId, market.outcomes) || '').trim() : '';

      // Predict 買一
      const obByToken = market.orderbookByToken || {};
      const predictOb =
        tokenId && obByToken[tokenId]
          ? obByToken[tokenId]
          : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
      const pBids = predictOb.bidsTop3 || [];
      const pBuyOne = pBids[0];
      const pBuyPrice = pBuyOne != null ? pBuyOne[0] : null;

      // Polymarket 買一（多選項市場需依 outcome 取對應子市場）
      const polyOb = (outcomeName && polyOrderbookByMarket?.[`${marketId}::${outcomeName}`]) || polyOrderbookByMarket?.[marketId];
      const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
      const polyBuyOne = polyBids[0];
      const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;
      const polyBuySize = polyBuyOne != null ? polyBuyOne[1] : 0;

      // 依 Poly 買一 與 Predict 買一 判斷安全等級：安全／危險／均衡（可設價差閾值，價差內視為安全）
      let polyBuyStatus = null;
      if (pBuyPrice != null && polyBuyPrice != null) {
        const eps = 0.0001;
        const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;
        const diff = Math.abs(polyBuyPrice - pBuyPrice);
        if (spreadThreshold > 0 && diff <= spreadThreshold) {
          polyBuyStatus = 'safe';
        } else if (polyBuyPrice - pBuyPrice > eps) polyBuyStatus = 'safe';
        else if (pBuyPrice - polyBuyPrice > eps) polyBuyStatus = 'danger';
        else polyBuyStatus = 'balanced';
      }

      const shouldCancelForDanger = autoCancelDanger && polyBuyStatus === 'danger';
      const shouldCancelForBalancedLow =
        autoCancelBalancedLowBook &&
        polyBuyStatus === 'balanced' &&
        typeof polyBuySize === 'number' &&
        polyBuySize < (balancedLowBookMinShares || 3000);

      // 當 Polymarket 買一 shares 不足該筆掛單 shares 的 2 倍時，只撤該筆單（與你看到的 Poly 盤口一致，避免誤撤）
      if (autoCancelBuyOneBelowOrder2x && typeof polyBuySize === 'number' && polyBuySize >= 0) {
        list.forEach((item) => {
          const id = item?.id;
          if (!id || handled.has(id)) return;
          const orderSize = Number(item.amount) || 0;
          if (orderSize <= 0) return;
          if (polyBuySize < orderSize * 2) {
            handled.add(id);
            onRemoveOrder(item);
          }
        });
      }

      if (!shouldCancelForDanger && !shouldCancelForBalancedLow) return;

      // 當市場判定為危險或均衡但 Poly 買一過薄時，撤銷該市場的所有掛單（每筆只處理一次）
      list.forEach((item) => {
        const id = item?.id;
        if (!id || handled.has(id)) return;
        handled.add(id);
        onRemoveOrder(item);
      });
    });
  }, [
    autoCancelDanger,
    autoCancelBalancedLowBook,
    balancedLowBookMinShares,
    autoCancelBuyOneBelowOrder2x,
    priceSpreadThresholdCents,
    marketIds,
    byMarket,
    markets,
    polyOrderbookByMarket,
    onRemoveOrder,
  ]);

  return (
    <>
      <div className="orders-list">
        {marketIds.map((marketId) => {
        const market = markets[marketId] || {};
        const question = market.question || market.title || `市場 ${marketId}`;
        const list = byMarket[marketId] || [];
        const fo = list[0];
        const foOutcome = fo?.order?.tokenId != null ? (getOutcomeName(fo.order.tokenId, market.outcomes) || '').trim() : '';
        const polyData = polymarketLinks?.[marketId] && polymarketByQuestion && ((foOutcome && polymarketByQuestion[`${marketId}::${question}::${foOutcome}`]) || polymarketByQuestion[`${marketId}::${question}`]);
        const linkUrl = linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '';
        const now = Date.now();
        const boostStart = market.boostStartsAt ? new Date(market.boostStartsAt).getTime() : null;
        const boostEnd = market.boostEndsAt ? new Date(market.boostEndsAt).getTime() : null;
        const inBoost = !!market.isBoosted && (!boostStart || now >= boostStart) && (!boostEnd || now <= boostEnd);
        const boostInMs = boostStart && boostStart > now ? boostStart - now : null;
        const boostInStr = boostInMs != null && !inBoost
          ? (() => {
              const h = Math.floor(boostInMs / 3600000);
              const m = Math.floor((boostInMs % 3600000) / 60000);
              return h > 0 ? `${h}h ${m}m` : `${m}m`;
            })()
          : null;
        const showBoost = inBoost || boostInStr;
        return (
          <div key={marketId} className="orders-market-block">
            <div className="orders-market-header">
              <div className="orders-market-icon">
                {market.imageUrl ? (
                  <img src={market.imageUrl} alt="" className="orders-market-icon-img" />
                ) : (
                  <span className="orders-market-icon-placeholder">
                    {question.slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <h3 className="orders-market-question" title={question}>
                {question}
              </h3>
              {showBoost && (
                <span className="orders-boost-badge" title={inBoost ? '市場積分增強中' : `Boost 將在 ${boostInStr} 後開始`}>
                  {inBoost ? 'Boost' : `Boost in: ${boostInStr}`}
                </span>
              )}
            </div>
            <div className="orders-poly-link-row">
              <label className="orders-poly-link-label">Polymarket 市場連結</label>
              <input
                type="url"
                className="orders-poly-link-input"
                placeholder="https://polymarket.com/event/..."
                value={linkUrl}
                onChange={(e) => setLinkDrafts((prev) => ({ ...prev, [marketId]: e.target.value }))}
              />
              <button
                type="button"
                className="orders-poly-link-save"
                onClick={async () => {
                  const url = (linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '').trim();
                  try {
                    await onSavePolyLink(marketId, url);
                    setLinkDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                    setSavedMarketId(marketId);
                    setTimeout(() => setSavedMarketId(null), 2500);
                  } catch (_) {}
                }}
              >
                保存
              </button>
              {savedMarketId === marketId && (
                <span className="orders-poly-link-saved">已保存</span>
              )}
              {polymarketLinks[marketId] && (
                <button
                  type="button"
                  className="orders-poly-link-delete"
                  onClick={async () => {
                    try {
                      await onDeletePolyLink(marketId);
                      setLinkDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                    } catch (_) {}
                  }}
                  title="刪除此 Poly 連結"
                >
                  删除
                </button>
              )}
            </div>
            <div className="orders-poly-link-row">
              <label className="orders-poly-link-label">Poly 子市場（選填）</label>
              <input
                type="text"
                className="orders-poly-link-input"
                placeholder="自動 或填 LIV、SUN、DRAW、ARS…"
                style={{ maxWidth: 160 }}
                value={polyOutcomeDrafts[marketId] ?? polyOutcomes?.[marketId] ?? ''}
                onChange={(e) => setPolyOutcomeDrafts((prev) => ({ ...prev, [marketId]: e.target.value }))}
              />
              <button
                type="button"
                className="orders-poly-link-save"
                onClick={async () => {
                  const v = (polyOutcomeDrafts[marketId] ?? polyOutcomes?.[marketId] ?? '').toString().trim();
                  if (onSavePolyOutcome) await onSavePolyOutcome(marketId, v);
                  setPolyOutcomeDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                }}
              >
                保存
              </button>
              {(() => {
                const linkUrl = linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '';
                const opts = polyOptionsFromApi?.[marketId] || parsePolyMarketOptions(linkUrl);
                if (!opts || opts.length < 1) return null;
                const current = polyOutcomeOverrides?.[marketId] ?? polyOutcomes?.[marketId] ?? '';
                const currentNorm = current.toString().trim().toUpperCase();
                const viewMode = polyViewMode?.[marketId] || 'yes';
                return (
                  <span className="predict-best-force-btns" style={{ marginLeft: 12 }}>
                    <span className="positions-query-desc" style={{ marginRight: 4 }}>強制顯示：</span>
                    {opts.map((opt) => {
                      const o = String(opt).trim().toUpperCase();
                      return (
                        <button
                          key={o}
                          type="button"
                          className={`predict-force-btn ${currentNorm === o ? 'active' : ''}`}
                          onClick={() => onSetPolyOverride?.(marketId, currentNorm === o ? '' : o)}
                          title={currentNorm === o ? `當前 ${o}，點擊清除` : `顯示 ${o}`}
                        >
                          {o}
                        </button>
                      );
                    })}
                    {onSetPolyViewMode && (
                      <>
                        <span className="predict-force-sep">|</span>
                        <button
                          type="button"
                          className={`predict-force-btn ${viewMode === 'yes' ? 'active' : ''}`}
                          onClick={() => onSetPolyViewMode(marketId, viewMode === 'yes' ? '' : 'yes')}
                          title="只看 Yes"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`predict-force-btn ${viewMode === 'no' ? 'active' : ''}`}
                          onClick={() => onSetPolyViewMode(marketId, viewMode === 'no' ? '' : 'no')}
                          title="只看 No"
                        >
                          No
                        </button>
                      </>
                    )}
                  </span>
                );
              })()}
            </div>
            <div className="orders-poly-link-row">
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!priceRuleEnabledByMarket?.[marketId]}
                  onChange={(e) => onTogglePriceRule && onTogglePriceRule(marketId, e.target.checked)}
                />
                僅對此市場啟用「Poly 買一價格 + 我掛單價格 &gt; 門檻就自動撤 Buy 單」
              </label>
            </div>
            <div className="orders-poly-link-row">
              <label className="orders-poly-link-label">開賽時間</label>
              <input
                type="datetime-local"
                className="orders-poly-link-input"
                style={{ maxWidth: 220 }}
                value={(startTimeDrafts[marketId] ?? marketStartTimes[marketId] ?? '').toString().slice(0, 16)}
                onChange={(e) => setStartTimeDrafts((prev) => ({ ...prev, [marketId]: e.target.value || undefined }))}
              />
              <button
                type="button"
                className="orders-poly-link-save"
                onClick={async () => {
                  const v = (startTimeDrafts[marketId] ?? marketStartTimes[marketId] ?? '').toString().trim().slice(0, 16);
                  const iso = v || '';
                  if (onSaveStartTime) await onSaveStartTime(marketId, iso);
                  setStartTimeDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                }}
              >
                保存
              </button>
              <span className="positions-query-desc" style={{ marginLeft: 4 }}>留空則不自動撤單</span>
            </div>
            <div className="orders-poly-link-row">
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!cancelBeforeStartByMarket?.[marketId]}
                  onChange={(e) => onToggleCancelBeforeStart && onToggleCancelBeforeStart(marketId, e.target.checked)}
                />
                開賽前 1 小時自動撤銷該市場全部訂單（需已填開賽時間）
              </label>
            </div>
            <div className="orders-table-wrap">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Side</th>
                    <th>Outcome</th>
                    <th>Price</th>
                    <th>Shares</th>
                    <th>Filled</th>
                    <th>Value</th>
                    <th>Expiry</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => {
                    const order = item.order || {};
                    const isBuy = order.side === 0;
                    const side = isBuy ? 'Buy' : 'Sell';
                    const tokenId = order.tokenId;
                    const outcomeName = getOutcomeName(tokenId, market.outcomes);
                    const isYes = String(outcomeName).toLowerCase() === 'yes';
                    const makerAmt = Number(order.makerAmount) || 0;
                    const takerAmt = Number(order.takerAmount) || 0;
                    // 價格 = USDT / shares (0-1)
                    // Buy: maker=USDT, taker=shares => price = maker/taker
                    // Sell: maker=shares, taker=USDT => price = taker/maker
                    const priceDecimal =
                      isBuy
                        ? takerAmt > 0 ? makerAmt / takerAmt : null
                        : makerAmt > 0 ? takerAmt / makerAmt : null;
                    const priceCents =
                      priceDecimal != null ? priceDecimal * 100 : null;
                    const priceStr =
                      priceCents != null && !Number.isNaN(priceCents)
                        ? `${priceCents.toFixed(1)}¢`
                        : '—';
                    // 檢查是否在買一/賣一
                    // 根據 tokenId 取得該 outcome 的 bestBid/bestAsk
                    const tokenPrices = market.bestPricesByToken?.[tokenId] || {};
                    const { bestBid, bestAsk } = tokenPrices;
                    let notAtBest = false;
                    if (priceDecimal != null) {
                      if (isBuy && bestBid != null) {
                        // 買單：價格應等於 bestBid 才算買一
                        notAtBest = Math.abs(priceDecimal - bestBid) > 0.001;
                      } else if (!isBuy && bestAsk != null) {
                        // 賣單：價格應等於 bestAsk 才算賣一
                        notAtBest = Math.abs(priceDecimal - bestAsk) > 0.001;
                      }
                    }
                    const amountNum = Number(item.amount) || 0;
                    const amountHuman =
                      amountNum >= 1e10 ? amountNum / 1e18 : amountNum;
                    const sharesStr =
                      amountNum > 0
                        ? amountHuman >= 1
                          ? amountHuman.toFixed(2)
                          : amountHuman.toString()
                        : '—';
                    const filledNum = Number(item.amountFilled) || 0;
                    const filledHuman =
                      filledNum >= 1e10 ? filledNum / 1e18 : filledNum;
                    const filledStr =
                      filledNum > 0 ? filledHuman.toFixed(2) : '0';
                    const valueNum =
                      priceCents != null && amountHuman > 0
                        ? (priceCents / 100) * amountHuman
                        : null;
                    const valueStr =
                      valueNum != null && !Number.isNaN(valueNum)
                        ? `$${valueNum.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : '—';
                    const exp = order.expiration;
                    const expiryStr =
                      exp && Number(exp) > 0
                        ? (() => {
                            const d = new Date(Number(exp) * 1000);
                            return Number.isNaN(d.getTime())
                              ? '—'
                              : d.toLocaleDateString();
                          })()
                        : '—';
                    const sellLink = `https://predict.fun/market/${marketId}`;
                    const currentBest = isBuy ? bestBid : bestAsk;
                    const currentBestStr = currentBest != null ? `${(currentBest * 100).toFixed(1)}¢` : '—';
                    // 比較 Predict 掛單價格與 Poly 買一/賣一
                    let polySafeStatus = null;
                    if (polyData && priceDecimal != null) {
                      const polyKey = `${isBuy ? 'buy' : 'sell'}${isYes ? 'Yes' : 'No'}`;
                      const polyEntry = polyData[polyKey];
                      if (polyEntry && polyEntry.price != null) {
                        const polyPrice = polyEntry.price;
                        const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;
                        const diff = Math.abs(priceDecimal - polyPrice);
                        if (spreadThreshold > 0 && diff <= spreadThreshold) {
                          polySafeStatus = 'safe';
                        } else if (isBuy) {
                          polySafeStatus = priceDecimal < polyPrice ? 'safe' : 'danger';
                        } else {
                          polySafeStatus = priceDecimal > polyPrice ? 'safe' : 'danger';
                        }
                      }
                    }
                    const rowKey = getOrderKeyForStatus(item, item._orderIndex ?? 0);
                    const isHighlighted = rowKey && highlightedOrderKeys[rowKey];
                    return (
                      <tr
                        ref={(el) => {
                          if (!rowKey || !el) return;
                          orderRowRefs.current[rowKey] = el;
                        }}
                        data-order-key={rowKey || undefined}
                        key={rowKey || item.id || order.hash || Math.random()}
                        className={`${notAtBest ? 'orders-row-not-best' : ''} ${isHighlighted ? 'orders-row-new' : ''}`}
                        title={notAtBest ? `不在${isBuy ? '買一' : '賣一'}，當前${outcomeName} ${isBuy ? '買一' : '賣一'}=${currentBestStr}` : ''}
                      >
                        <td className="orders-cell-side">{side}</td>
                        <td
                          className={`orders-cell-outcome ${isYes ? 'outcome-yes' : 'outcome-no'}`}
                        >
                          {outcomeName}
                        </td>
                        <td className="orders-cell-price">
                          {priceStr}
                          {notAtBest && <span className="orders-not-best-badge">!</span>}
                        </td>
                        <td className="orders-cell-shares">{sharesStr}</td>
                        <td className="orders-cell-filled">{filledStr}</td>
                        <td className="orders-cell-value">{valueStr}</td>
                        <td className="orders-cell-expiry">{expiryStr}</td>
                        <td className="orders-cell-action">
                          {polySafeStatus && (
                            <span
                              className={`orders-poly-status orders-poly-status-${polySafeStatus}`}
                              title={polySafeStatus === 'safe' ? '與 Poly 比較：安全' : '與 Poly 比較：危險'}
                            >
                              {polySafeStatus === 'safe' ? '安全' : '危險'}
                            </span>
                          )}
                          {notAtBest && onReplaceAtBest && (
                            <button
                              type="button"
                              className="orders-replace-btn"
                              title={`一鍵撤單並掛到當前${isBuy ? '買一' : '賣一'}（${currentBestStr}）`}
                              disabled={replaceOrderId === item.id}
                              onClick={() =>
                                onReplaceAtBest({
                                  orderId: item.id,
                                  marketId,
                                  tokenId: order.tokenId,
                                  side: order.side,
                                  sharesWei: item.amount,
                                })
                              }
                            >
                              {replaceOrderId === item.id ? '處理中…' : `一鍵撤單並掛到${isBuy ? '買一' : '賣一'}`}
                            </button>
                          )}
                          {onAddToAutoTrading && (
                            <button
                              type="button"
                              className="orders-auto-btn"
                              title="加入全自動掛單撤單監控"
                              onClick={() => onAddToAutoTrading(item)}
                            >
                              全自動
                            </button>
                          )}
                          {onRemoveOrder && (
                            <button
                              type="button"
                              className="orders-remove-btn"
                              title="直接撤銷此筆掛單"
                              disabled={removingOrderId === item.id}
                              onClick={() => onRemoveOrder(item)}
                            >
                              {removingOrderId === item.id ? '撤單中…' : '撤單'}
                            </button>
                          )}
                          <a
                            href={sellLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="orders-cancel-link"
                            title="前往 Predict 取消／管理"
                          >
                            取消
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(() => {
              const firstOrder = list[0];
              const tokenId = firstOrder?.order?.tokenId;
              const outcomeName = tokenId != null ? (getOutcomeName(tokenId, market.outcomes) || '').trim() : '';

              // Predict 盤口：依掛單 outcome 取該 token 的 orderbook（若不存在則退回整體 YES 盤口）
              const obByToken = market.orderbookByToken || {};
              const predictOb =
                tokenId && obByToken[tokenId]
                  ? obByToken[tokenId]
                  : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
              const pAsks = predictOb.asksTop3 || [];
              const pBids = predictOb.bidsTop3 || [];
              const pSellOne = pAsks[0];
              const pBuyOne = pBids[0];
              const pSellPrice = pSellOne != null ? pSellOne[0] : null;
              const pSellSize = pSellOne != null ? pSellOne[1] : 0;
              const pBuyPrice = pBuyOne != null ? pBuyOne[0] : null;
              const pBuySize = pBuyOne != null ? pBuyOne[1] : 0;
              const pSellCents = pSellPrice != null ? (pSellPrice * 100).toFixed(1) : null;
              const pBuyCents = pBuyPrice != null ? (pBuyPrice * 100).toFixed(1) : null;
              const pHasDepth = pSellOne != null || pBuyOne != null;
              const pSellSizeStr =
                typeof pSellSize === 'number'
                  ? pSellSize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : String(pSellSize);
              const pBuySizeStr =
                typeof pBuySize === 'number'
                  ? pBuySize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : String(pBuySize);

              // Polymarket 盤口：取已計算好的 polyOrderbookByMarket（多選項依 outcome 如 Nuggets→DEN）
              const polyOb = (outcomeName && polyOrderbookByMarket?.[`${marketId}::${outcomeName}`]) || polyOrderbookByMarket?.[marketId];
              const polyAsks = (polyOb?.asks || []).slice().sort((a, b) => (a[0] || 0) - (b[0] || 0));
              const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
              const polySellOne = polyAsks[0];
              const polyBuyOne = polyBids[0];
              const polySellPrice = polySellOne != null ? polySellOne[0] : null;
              const polySellSize = polySellOne != null ? polySellOne[1] : 0;
              const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;
              const polyBuySize = polyBuyOne != null ? polyBuyOne[1] : 0;
              const polySellCents = polySellPrice != null ? (polySellPrice * 100).toFixed(1) : null;
              const polyBuyCents = polyBuyPrice != null ? (polyBuyPrice * 100).toFixed(1) : null;
              const polyHasDepth = polySellOne != null || polyBuyOne != null;
              const polySellSizeStr =
                typeof polySellSize === 'number'
                  ? polySellSize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : String(polySellSize);
              const polyBuySizeStr =
                typeof polyBuySize === 'number'
                  ? polyBuySize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : String(polyBuySize);

              // 依 Poly 買一 與 Predict 買一 判斷安全等級：安全／危險／均衡（可設價差閾值）
              let polyBuyStatus = null;
              if (pBuyPrice != null && polyBuyPrice != null) {
                const eps = 0.0001;
                const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;
                const diff = Math.abs(polyBuyPrice - pBuyPrice);
                if (spreadThreshold > 0 && diff <= spreadThreshold) polyBuyStatus = 'safe';
                else if (polyBuyPrice - pBuyPrice > eps) polyBuyStatus = 'safe';
                else if (pBuyPrice - polyBuyPrice > eps) polyBuyStatus = 'danger';
                else polyBuyStatus = 'balanced';
              }

              if (!pHasDepth && !polyHasDepth) return null;

              return (
                <div className="predict-best-row">
                  {pHasDepth && (
                    <div className="predict-best-col">
                      <span className="predict-best-label">
                        Predict 買一／賣一{outcomeName ? `（${outcomeName}）` : ''}
                      </span>
                      <div className="predict-best-table">
                        <div className="predict-best-tr predict-best-ask">
                          <span className="predict-best-td-label">賣一</span>
                          <span className="predict-best-td-price">
                            {pSellCents != null ? `${pSellCents}¢` : '—'}
                          </span>
                          <span className="predict-best-td-shares">
                            {pSellOne != null ? pSellSizeStr : '—'}
                          </span>
                        </div>
                        <div className="predict-best-tr predict-best-bid">
                          <span className="predict-best-td-label">買一</span>
                          <span className="predict-best-td-price">
                            {pBuyCents != null ? `${pBuyCents}¢` : '—'}
                          </span>
                          <span className="predict-best-td-shares">
                            {pBuyOne != null ? pBuySizeStr : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {polyHasDepth && (
                    <div className="predict-best-col">
                      <div className="predict-best-label-row">
                        <span className="predict-best-label">
                          Polymarket 買一／賣一{outcomeName ? `（${outcomeName}）` : ''}
                        </span>
                        {(() => {
                          const opts = polyOptionsFromApi?.[marketId] || parsePolyMarketOptions(linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '');
                          const current = polyOutcomeOverrides?.[marketId] ?? polyOutcomes?.[marketId] ?? '';
                          const currentNorm = current.toString().trim().toUpperCase();
                          const viewMode = polyViewMode?.[marketId] || 'yes';
                          return (
                            <span className="predict-best-force-btns">
                              {opts && opts.length >= 1 && onSetPolyOverride && opts.map((opt) => {
                                const o = String(opt).trim().toUpperCase();
                                return (
                                  <button
                                    key={o}
                                    type="button"
                                    className={`predict-force-btn ${currentNorm === o ? 'active' : ''}`}
                                    onClick={() => onSetPolyOverride(marketId, currentNorm === o ? '' : o)}
                                    title={currentNorm === o ? `當前顯示 ${o}，點擊清除` : `強制顯示 ${o}`}
                                  >
                                    {o}
                                  </button>
                                );
                              })}
                              {opts && opts.length >= 1 && onSetPolyViewMode && (
                                <>
                                  <span className="predict-force-sep">|</span>
                                  <button
                                    type="button"
                                    className={`predict-force-btn ${viewMode === 'yes' ? 'active' : ''}`}
                                    onClick={() => onSetPolyViewMode(marketId, viewMode === 'yes' ? '' : 'yes')}
                                    title="只看 Yes 市場"
                                  >
                                    Yes
                                  </button>
                                  <button
                                    type="button"
                                    className={`predict-force-btn ${viewMode === 'no' ? 'active' : ''}`}
                                    onClick={() => onSetPolyViewMode(marketId, viewMode === 'no' ? '' : 'no')}
                                    title="只看 No 市場"
                                  >
                                    No
                                  </button>
                                </>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="predict-best-table">
                        <div className="predict-best-tr predict-best-ask">
                          <span className="predict-best-td-label">賣一</span>
                          <span className="predict-best-td-price">
                            {polySellCents != null ? `${polySellCents}¢` : '—'}
                          </span>
                          <span className="predict-best-td-shares">
                            {polySellOne != null ? polySellSizeStr : '—'}
                          </span>
                        </div>
                        <div className="predict-best-tr predict-best-bid">
                          <span className="predict-best-td-label">買一</span>
                          <span className="predict-best-td-price">
                            {polyBuyCents != null ? `${polyBuyCents}¢` : '—'}
                          </span>
                          <span className="predict-best-td-shares">
                            {polyBuyOne != null ? polyBuySizeStr : '—'}
                          </span>
                          {polyBuyStatus && (
                            <span className={`predict-best-status predict-best-status-${polyBuyStatus}`}>
                              {polyBuyStatus === 'safe' ? '安全' : polyBuyStatus === 'danger' ? '危險' : '均衡'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <PolymarketDepthInline
              polyData={polyData}
              polyOrderbookItem={(foOutcome && polyOrderbookByMarket?.[`${marketId}::${foOutcome}`]) || polyOrderbookByMarket?.[marketId]}
              loading={polymarketLoading}
              orderList={list}
              market={market}
            />
          </div>
        );
        })}
      </div>
      {statusSummary.length > 0 && (
        <div
          className="orders-status-floating"
          style={statusPos.x != null ? { left: statusPos.x, top: statusPos.y } : undefined}
          onMouseDown={handleStatusMouseDown}
        >
          <div className="orders-status-title">掛單風險速覽</div>
          {orders.length > 0 && (
            <div className="orders-status-jump">
              <button
                type="button"
                className="orders-status-jump-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => scrollToOrderIndex(0, 'start')}
              >
                到底
              </button>
              <button
                type="button"
                className="orders-status-jump-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => scrollToOrderIndex(Math.floor(orders.length / 2))}
              >
                中間
              </button>
              <button
                type="button"
                className="orders-status-jump-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => scrollToOrderIndex(orders.length - 1, 'end')}
              >
                到顶
              </button>
            </div>
          )}
          <ol className="orders-status-list">
            {statusSummary.map((s, idx) => (
              <li key={s.key || idx} className="orders-status-item">
                <span
                  className={`orders-status-pill orders-status-pill-${s.riskStatus || 'none'} ${s.orderKey ? 'orders-status-pill-clickable' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusItemClick(s);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {s.text}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </>
  );
}

// 全自動刷分神奇 — 獨立分頁、獨立密鑰
function ScoreGrindingPage() {
  const [sgConfig, setSgConfig] = useState({ apiKey: '', accounts: [], enabled: false, markets: [], status: {}, hasApiKey: false });
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [sgConfigLoaded, setSgConfigLoaded] = useState(false);
  const [addMarket, setAddMarket] = useState({ marketId: '', outcomeName: 'No', shares: 100, accountAddress: '' });
  const [addMarketError, setAddMarketError] = useState('');

  const loadSgConfig = useCallback(async () => {
    try {
      const resp = await fetch(api('/api/score-grinding'));
      const body = await resp.json();
      if (body?.success && body?.data) setSgConfig({ ...body.data, status: body.status || {} });
    } catch (_) {}
    finally { setSgConfigLoaded(true); }
  }, []);

  useEffect(() => { loadSgConfig(); }, [loadSgConfig]);

  const patchSgConfig = useCallback(async (updates, includeApiKey = false) => {
    try {
      const payload = { ...updates };
      if (includeApiKey && apiKeyInput.trim()) payload.apiKey = apiKeyInput.trim();
      const resp = await fetch(api('/api/score-grinding'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await resp.json();
      if (body?.success) {
        if (includeApiKey) setApiKeyInput('');
        loadSgConfig();
      }
    } catch (_) {}
  }, [apiKeyInput, loadSgConfig]);

  const handleAddAccount = useCallback((acc) => {
    const accounts = [...(sgConfig.accounts || []), { ...acc, id: acc.id || generateId() }];
    patchSgConfig({ accounts });
  }, [sgConfig.accounts, patchSgConfig]);

  const handleUpdateAccount = useCallback((id, updates) => {
    const accounts = (sgConfig.accounts || []).map((a) => (a.id === id ? { ...a, ...updates } : a));
    patchSgConfig({ accounts });
  }, [sgConfig.accounts, patchSgConfig]);

  const handleDeleteAccount = useCallback((id) => {
    const accounts = (sgConfig.accounts || []).filter((a) => a.id !== id);
    patchSgConfig({ accounts });
  }, [sgConfig.accounts, patchSgConfig]);

  const handleAddMarket = useCallback(async () => {
    const { marketId, outcomeName, shares, accountAddress } = addMarket;
    if (!marketId.trim() || !accountAddress.trim()) {
      setAddMarketError('請填寫市場 ID 並選擇帳戶');
      return;
    }
    setAddMarketError('');
    try {
      const resp = await fetch(api('/api/score-grinding/add-market'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketId: marketId.trim(), outcomeName: outcomeName || 'No', shares: Math.max(100, parseInt(shares, 10) || 100), accountAddress: accountAddress.trim() }),
      });
      const body = await resp.json();
      if (body?.success) {
        loadSgConfig();
        setAddMarket((prev) => ({ ...prev, marketId: '', outcomeName: 'No', shares: 100, accountAddress: '' }));
      } else {
        setAddMarketError(body?.message || '新增失敗');
      }
    } catch (e) {
      setAddMarketError(e?.message || '新增失敗');
    }
  }, [addMarket, loadSgConfig]);

  const accountsWithAddress = (sgConfig.accounts || []).map((a) => ({ ...a, address: a.address || '' })).filter((a) => a.address);

  return (
    <div className="positions-page">
      <header className="positions-header">
        <h1>全自動刷分神奇</h1>
        <p className="positions-subtitle">獨立密鑰與帳戶，與持倉查詢完全分離</p>
      </header>
      <section className="positions-query-card">
        <h2 className="positions-section-title">專用密鑰</h2>
        <p className="positions-query-desc">本分頁使用獨立 API Key 與帳戶，不與上方持倉查詢共用。</p>
        <div className="positions-form">
          <div className="positions-form-row">
            <label className="positions-label">API Key</label>
            <input
              type="password"
              className="positions-input"
              placeholder={sgConfig.hasApiKey ? '已設定，輸入新值可覆蓋' : '請輸入 Predict API Key'}
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              autoComplete="off"
            />
            {apiKeyInput.trim() && (
              <button type="button" className="positions-retry-btn" style={{ marginLeft: 8 }} onClick={() => patchSgConfig({}, true)}>
                保存
              </button>
            )}
            {sgConfig.hasApiKey && !apiKeyInput && <span className="positions-query-desc" style={{ marginLeft: 8 }}>已設定 ***</span>}
          </div>
        </div>
      </section>
      <section className="positions-account-card">
        <h2 className="positions-section-title">專用帳戶</h2>
        <p className="positions-query-desc">添加用於刷分的帳戶，需填寫 Privy 私鑰以支援掛單。</p>
        <AccountManager
          accounts={accountsWithAddress}
          currentAddress=""
          onSelect={() => {}}
          onAdd={handleAddAccount}
          onUpdate={handleUpdateAccount}
          onDelete={handleDeleteAccount}
        />
      </section>
      <section className="positions-list-section">
        <h2 className="positions-section-title">監控市場</h2>
        <p className="positions-query-desc" style={{ marginBottom: 12 }}>
          輪詢 1～3 秒：買一 shares ≥ 1000 且掛單在買一則保持；&lt; 1000 立刻撤單；不在買一時若買一 ≥ 1000 則掛單。股數 min 100、max 市場買一 30%。1 分鐘無法讀取則撤銷該市場所有掛單。
        </p>
        <div className="positions-form-row">
          <label className="positions-label">啟用</label>
          <input type="checkbox" checked={sgConfig.enabled} onChange={(e) => patchSgConfig({ enabled: e.target.checked })} />
        </div>
        <div className="positions-form-row">
          <label className="positions-label">輪詢間隔（秒）</label>
          <select value={sgConfig.pollIntervalSec ?? 2} onChange={(e) => patchSgConfig({ pollIntervalSec: Number(e.target.value) })} style={{ width: 100 }}>
            <option value={1}>1 秒</option>
            <option value={2}>2 秒</option>
            <option value={3}>3 秒</option>
            <option value={5}>5 秒</option>
            <option value={10}>10 秒</option>
          </select>
          <span className="positions-query-desc" style={{ marginLeft: 8 }}>1～3 秒建議，可自訂</span>
        </div>
        <div className="score-grinding-add-row">
          <input type="text" className="positions-input" placeholder="市場 ID" value={addMarket.marketId} onChange={(e) => { setAddMarket((m) => ({ ...m, marketId: e.target.value })); setAddMarketError(''); }} style={{ width: 120 }} />
          <select className="positions-input" value={addMarket.outcomeName} onChange={(e) => setAddMarket((m) => ({ ...m, outcomeName: e.target.value }))} style={{ width: 70 }}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          <input type="number" className="positions-input" placeholder="股數 min100" value={addMarket.shares} onChange={(e) => setAddMarket((m) => ({ ...m, shares: e.target.value }))} style={{ width: 90 }} min={100} />
          <select className="positions-input" value={addMarket.accountAddress} onChange={(e) => setAddMarket((m) => ({ ...m, accountAddress: e.target.value }))} style={{ width: 140 }}>
            <option value="">選擇帳戶</option>
            {accountsWithAddress.map((a) => (
              <option key={a.id || a.address} value={a.address}>{a.note || `${a.address?.slice(0, 8)}…`}</option>
            ))}
          </select>
          <button type="button" className="positions-retry-btn" onClick={handleAddMarket}>加入</button>
        </div>
        {addMarketError && <p className="positions-orders-error" style={{ marginBottom: 12 }}>{addMarketError}</p>}
        {sgConfig.markets?.length === 0 ? (
          <p className="positions-query-desc">尚未添加市場，請填寫市場 ID 並選擇 Yes/No 後加入</p>
        ) : (
          <ul className="auto-trading-list">
            {sgConfig.markets.map((m) => {
              const key = `sg_${m.marketId}_${m.tokenId}`;
              const st = sgConfig.status?.[key] || {};
              return (
                <li key={m.id || key} className="auto-trading-item">
                  <span>市場 {m.marketId} · {m.outcomeName} · {m.shares} 股 · {m.accountNote || m.accountAddress?.slice(0, 8)}…</span>
                  {st.blacklisted && <span className="auto-trading-badge auto-trading-blacklist">黑名單 30 分鐘</span>}
                  {st.lastError && <span className="auto-trading-badge auto-trading-blacklist" title={st.lastError} style={{ cursor: 'help' }}>⚠ {st.lastError.slice(0, 20)}{st.lastError.length > 20 ? '…' : ''}</span>}
                  <button type="button" className="positions-retry-btn" style={{ marginLeft: 8, padding: '4px 10px', fontSize: 12 }} onClick={() => patchSgConfig({ markets: sgConfig.markets.filter((x) => (x.id || `${x.marketId}_${x.tokenId}_${x.accountAddress}`) !== (m.id || `${m.marketId}_${m.tokenId}_${m.accountAddress}`)) })}>移除</button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export default function App() {
  const [mainPage, setMainPage] = useState('positions');
  const [apiKey, setApiKey] = useState('');
  const [address, setAddress] = useState('');
  const [remember, setRemember] = useState(true);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [activeTab, setActiveTab] = useState('positions');
  const [pollIntervalSec, setPollIntervalSec] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_POLL), 10);
      return Number.isFinite(v) && POLL_OPTIONS.some((o) => o.value === v) ? v : 0;
    } catch {
      return 0;
    }
  });
  const [polyPollIntervalSec, setPolyPollIntervalSec] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_POLY_POLL), 10);
      return Number.isFinite(v) && POLY_POLL_OPTIONS.some((o) => o.value === v) ? v : 5;
    } catch {
      return 5;
    }
  });
  const [orders, setOrders] = useState([]);
  const [ordersMarkets, setOrdersMarkets] = useState({});
  const [ordersPolyOrderbook, setOrdersPolyOrderbook] = useState({});
  const [ordersError, setOrdersError] = useState(null);
  const [replaceOrderId, setReplaceOrderId] = useState(null);
  const [removingOrderId, setRemovingOrderId] = useState(null);
  const [polymarketByQuestion, setPolymarketByQuestion] = useState({});
  const [polymarketLoading, setPolymarketLoading] = useState(false);
  const [polymarketError, setPolymarketError] = useState(null);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [polymarketLinks, setPolymarketLinks] = useState({});
  const [marketStartTimes, setMarketStartTimes] = useState({});
  const [cancelBeforeStartByMarket, setCancelBeforeStartByMarket] = useState({});
  const [polyOutcomes, setPolyOutcomes] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [autoTrading, setAutoTrading] = useState({ enabled: false, markets: [], status: {} });
  const [telegramAlerts, setTelegramAlerts] = useState({
    orderPlaced: true,
    orderCancelled: true,
    orderFilled: true,
    orderDanger: true,
    notAtBest: true,
  });
  const [telegramPollSec, setTelegramPollSec] = useState(120);
  const [wecomWebhookUrl, setWecomWebhookUrl] = useState('');
  const [polyAutoCancel, setPolyAutoCancel] = useState({
    danger: false,
    balancedLowBook: false,
    balancedLowBookMinShares: 3000,
    cancelOnPartialFill: false,
    buyOneBelowOrder2x: false,
    cancelAllOnPartialFillOver: 0,
    priceSumThresholdCents: 0,
    priceSpreadThresholdCents: 0,
  });
  const [priceRuleEnabledByMarket, setPriceRuleEnabledByMarket] = useState({});
  const [polyOutcomeOverrides, setPolyOutcomeOverrides] = useState({});
  const [polyOptionsFromApi, setPolyOptionsFromApi] = useState({});
  const [polyViewMode, setPolyViewMode] = useState({});

  // 當前帳戶備註
  const currentAccountNote = useMemo(() => {
    const addrLower = String(address || '').toLowerCase();
    if (!addrLower) return '';
    const found = savedAccounts.find(
      (a) => String(a.address || '').toLowerCase() === addrLower
    );
    return found?.note || '';
  }, [savedAccounts, address]);

  // 載入後端配置（API Key + 帳戶列表）
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch(api('/api/config'));
        if (!resp.ok) return;
        const body = await resp.json();
        if (!cancelled && body?.success && body?.data) {
          const {
            apiKey: k,
            accounts: accts,
            currentAddress: curr,
            telegramAlerts: alerts,
            telegramPollIntervalSec: tSec,
            wecomWebhookUrl: wcUrl,
            polyAutoCancel: polyCfg,
          } = body.data;
          setApiKey(k || '');
          setSavedAccounts(Array.isArray(accts) ? accts : []);
          if (tSec != null) {
            setTelegramPollSec(Number(tSec) || 120);
          }
          if (alerts && typeof alerts === 'object') {
            setTelegramAlerts((prev) => ({
              orderPlaced: true,
              orderCancelled: true,
              orderFilled: true,
              orderDanger: true,
              notAtBest: true,
              ...prev,
              ...alerts,
            }));
          }
          if (typeof wcUrl === 'string') setWecomWebhookUrl(wcUrl || '');
          const addr = (curr || '').trim();
          setAddress(
            addr
              ? addr
              : Array.isArray(accts) && accts.length > 0
                ? accts[0].address || ''
                : ''
          );
          if (polyCfg && typeof polyCfg === 'object') {
            setPolyAutoCancel((prev) => ({
              danger: !!(polyCfg.danger ?? prev.danger),
              balancedLowBook: !!(polyCfg.balancedLowBook ?? prev.balancedLowBook),
              balancedLowBookMinShares:
                polyCfg.balancedLowBookMinShares != null
                  ? Number(polyCfg.balancedLowBookMinShares) || prev.balancedLowBookMinShares
                  : prev.balancedLowBookMinShares,
              cancelOnPartialFill: !!(polyCfg.cancelOnPartialFill ?? prev.cancelOnPartialFill),
              buyOneBelowOrder2x: !!(polyCfg.buyOneBelowOrder2x ?? prev.buyOneBelowOrder2x),
              cancelAllOnPartialFillOver:
                polyCfg.cancelAllOnPartialFillOver != null
                  ? Math.max(0, Math.min(1000000, Math.floor(Number(polyCfg.cancelAllOnPartialFillOver) || 0)))
                  : prev.cancelAllOnPartialFillOver,
              priceSumThresholdCents:
                polyCfg.priceSumThresholdCents != null
                  ? Math.max(0, Math.min(200, Math.floor(Number(polyCfg.priceSumThresholdCents) || 0)))
                  : prev.priceSumThresholdCents,
              priceSpreadThresholdCents:
                polyCfg.priceSpreadThresholdCents != null
                  ? Math.max(0, Math.min(50, Math.floor(Number(polyCfg.priceSpreadThresholdCents) || 0)))
                  : prev.priceSpreadThresholdCents ?? 0,
            }));
          }
        }
      } catch (_) {
        try {
          const k = localStorage.getItem(STORAGE_API_KEY) || '';
          const a = localStorage.getItem(STORAGE_ADDRESS) || '';
          const accts = localStorage.getItem(STORAGE_ACCOUNTS);
          if (!cancelled) {
            setApiKey(k);
            setAddress(a);
            setSavedAccounts(accts ? JSON.parse(accts) : []);
          }
        } catch (__) {}
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // 同步到後端（API Key、帳戶、當前地址）
  const syncConfig = useCallback(async (updates) => {
    try {
      const resp = await fetch(api('/api/config'), { method: 'GET' });
      if (!resp.ok) return;
      const body = await resp.json();
      const config = body?.data || {
        apiKey: '',
        accounts: [],
        currentAddress: '',
        telegramAlerts: { orderPlaced: true, orderCancelled: true, orderFilled: true, orderDanger: true, notAtBest: true },
        telegramPollIntervalSec: undefined,
        wecomWebhookUrl: '',
        polyAutoCancel: {},
      };
      const merged = {
        apiKey: updates.apiKey !== undefined ? updates.apiKey : config.apiKey,
        accounts: updates.accounts !== undefined ? updates.accounts : config.accounts,
        currentAddress: updates.currentAddress !== undefined ? updates.currentAddress : config.currentAddress,
        telegramAlerts: updates.telegramAlerts !== undefined ? updates.telegramAlerts : (config.telegramAlerts || { orderPlaced: true, orderCancelled: true, orderFilled: true, orderDanger: true, notAtBest: true }),
        telegramPollIntervalSec: updates.telegramPollIntervalSec !== undefined
          ? updates.telegramPollIntervalSec
          : (config.telegramPollIntervalSec !== undefined ? config.telegramPollIntervalSec : undefined),
        wecomWebhookUrl: updates.wecomWebhookUrl !== undefined ? updates.wecomWebhookUrl : (config.wecomWebhookUrl || ''),
        polyAutoCancel:
          updates.polyAutoCancel !== undefined
            ? { ...(config.polyAutoCancel || {}), ...updates.polyAutoCancel }
            : config.polyAutoCancel,
      };
      await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(merged),
      });
    } catch (_) {}
  }, []);

  // 載入 Polymarket 連結與開賽時間（單獨檔案，超過 7 天未使用已自動刪除）
  const loadPolyLinks = useCallback(async () => {
    try {
      const resp = await fetch(api('/api/polymarket-links'));
      if (!resp.ok) return;
      const body = await resp.json();
      if (body?.success && body?.data) {
        if (body.data.links) setPolymarketLinks(body.data.links);
        if (body.data.startTimes && typeof body.data.startTimes === 'object') setMarketStartTimes(body.data.startTimes);
        if (body.data.cancelBeforeStartByMarket && typeof body.data.cancelBeforeStartByMarket === 'object') setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
        if (body.data.polyOutcomes && typeof body.data.polyOutcomes === 'object') setPolyOutcomes(body.data.polyOutcomes);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (configLoaded) loadPolyLinks();
  }, [configLoaded, loadPolyLinks]);

  const loadAutoTrading = useCallback(async () => {
    try {
      const resp = await fetch(api('/api/auto-trading'));
      const body = await resp.json().catch(() => ({}));
      if (resp.ok && body?.success && body?.data) setAutoTrading({ ...body.data, status: body.status || {} });
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (configLoaded) loadAutoTrading();
  }, [configLoaded, loadAutoTrading]);

  const patchAutoTrading = useCallback(async (updates) => {
    try {
      const resp = await fetch(api('/api/auto-trading'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const body = await resp.json();
      if (body?.success) loadAutoTrading();
    } catch (_) {}
  }, [loadAutoTrading]);

  const addOrderToAutoTrading = useCallback((item) => {
    const order = item.order || {};
    const market = ordersMarkets[item.marketId];
    if (!market?.outcomes) return;
    const outcomeName = (() => {
      for (const o of market.outcomes) {
        if (String(o.onChainId) === String(order.tokenId)) return o.name || 'No';
      }
      return 'No';
    })();
    const amountNum = Number(item.amount) || 0;
    const shares = amountNum >= 1e10 ? Math.floor(amountNum / 1e18) : Math.floor(amountNum) || 100;
    const newMarket = {
      marketId: item.marketId,
      tokenId: order.tokenId,
      outcomeName,
      accountAddress: address.trim(),
      accountNote: currentAccountNote || '',
      shares: Math.max(1, shares),
    };
    const exists = autoTrading.markets.some(
      (m) => m.marketId === newMarket.marketId && m.tokenId === newMarket.tokenId && m.accountAddress === newMarket.accountAddress
    );
    if (exists) return;
    patchAutoTrading({ markets: [...autoTrading.markets, newMarket] });
  }, [ordersMarkets, address, currentAccountNote, autoTrading.markets, patchAutoTrading]);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (apiKey.trim()) h['x-api-key'] = apiKey.trim();
    return h;
  }, [apiKey]);

  // Cloudflare 部署時：在瀏覽器內用 Privy 私鑰簽名取得 JWT，再查挂单（私鑰不經伺服器）
  // 回傳 { token } 或 { error: string } 方便顯示具體失敗原因
  const getJwtForOrders = useCallback(async (apiKeyVal, addressVal, privateKeyVal) => {
    if (!API_BASE || !apiKeyVal?.trim() || !addressVal?.trim() || !privateKeyVal?.trim()) {
      return { error: '缺少 API Key、地址或 Privy 私鑰' };
    }
    try {
      const msgRes = await fetch(api('/api/auth/message'), { headers: { 'x-api-key': apiKeyVal.trim() } });
      const msgBody = await msgRes.json().catch(() => ({}));
      if (!msgRes.ok) {
        return { error: `取得簽名訊息失敗（${msgRes.status}）${msgBody?.message ? ': ' + msgBody.message : ''}` };
      }
      if (!msgBody?.data?.message) {
        return { error: '後端未回傳簽名用 message，請確認 Worker 已部署且 /api/auth/message 正常' };
      }
      let signature;
      try {
        signature = await signPredictAccountMessage(privateKeyVal.trim(), addressVal.trim(), msgBody.data.message);
      } catch (e) {
        return { error: `瀏覽器簽名失敗：${e?.message || '請確認私鑰格式為 0x 開頭 64 字元十六進位'}` };
      }
      const authRes = await fetch(api('/api/auth'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKeyVal.trim() },
        body: JSON.stringify({ signer: addressVal.trim(), signature, message: msgBody.data.message }),
      });
      const authBody = await authRes.json().catch(() => ({}));
      if (!authRes.ok) {
        return { error: `換取 JWT 失敗（${authRes.status}）${authBody?.message ? ': ' + authBody.message : ''}，請確認該私鑰對應此存款地址（Predict → Account → Settings → Export）` };
      }
      if (!authBody?.data?.token) {
        return { error: '後端未回傳 token' };
      }
      return { token: authBody.data.token };
    } catch (e) {
      return { error: `JWT 取得異常：${e?.message || String(e)}` };
    }
  }, []);

  const fetchPositions = useCallback(
    async (after = null, addrOverride = null) => {
      const addr = (addrOverride ?? address).trim();
      if (!addr) return { list: [], nextCursor: null };
      const url = after
        ? api(`/api/positions/${encodeURIComponent(addr)}?first=100&after=${encodeURIComponent(after)}`)
        : api(`/api/positions/${encodeURIComponent(addr)}?first=100`);
      const resp = await fetch(url, { headers: headers() });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.message || `持倉請求失敗 ${resp.status}`);
      }
      const body = await resp.json();
      const list = Array.isArray(body?.data) ? body.data : [];
      const nextCursor = body?.cursor ?? null;
      return { list, nextCursor };
    },
    [address, headers]
  );

  const fetchOrders = useCallback(async (addrOverride = null, jwt = null) => {
    const addr = (addrOverride ?? address).trim();
    if (!addr) return { list: [], markets: {}, polyOrderbook: {} };
    const orderHeaders = () => ({ ...headers(), ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) });
    // 先抓第 1 頁（Predict API 單次最多 100 筆），若有 cursor 再自動翻頁，最多抓到約 300 筆
    const base = api(`/api/orders/${encodeURIComponent(addr)}`);
    const firstPageUrl = `${base}?first=100`;
    const resp = await fetch(firstPageUrl, { headers: orderHeaders() });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(body?.message || `掛單請求失敗 ${resp.status}`);
    }

    let list = Array.isArray(body?.data) ? body.data : [];
    let markets = body?.markets && typeof body.markets === 'object' ? body.markets : {};
    let polyOrderbook =
      body?.polyOrderbook && typeof body.polyOrderbook === 'object' ? body.polyOrderbook : {};
    let cursor = body?.cursor ?? null;

    // 追加後續頁面（若有），避免只看到前 100 筆掛單
    let safety = 0;
    while (cursor && list.length < 300 && safety < 5) {
      const pageUrl = `${base}?first=100&after=${encodeURIComponent(cursor)}`;
      // eslint-disable-next-line no-await-in-loop
      const resp2 = await fetch(pageUrl, { headers: orderHeaders() });
      // eslint-disable-next-line no-await-in-loop
      const body2 = await resp2.json().catch(() => ({}));
      if (!resp2.ok) break;
      const pageList = Array.isArray(body2?.data) ? body2.data : [];
      if (pageList.length === 0) break;
      list = list.concat(pageList);
      if (body2?.markets && typeof body2.markets === 'object') {
        markets = { ...markets, ...body2.markets };
      }
      if (body2?.polyOrderbook && typeof body2.polyOrderbook === 'object') {
        polyOrderbook = { ...polyOrderbook, ...body2.polyOrderbook };
      }
      cursor = body2?.cursor ?? null;
      safety += 1;
    }

    return { list, markets, polyOrderbook };
  }, [address, headers]);

  const savePolymarketLink = useCallback(async (marketId, url) => {
    const trimmed = (url || '').trim();
    const nextLinks = { ...polymarketLinks };
    if (trimmed) nextLinks[marketId] = trimmed;
    else delete nextLinks[marketId];
    const payload = { links: nextLinks };
    if (trimmed) {
      const parsedStart = parseStartTimeFromPolyUrl(trimmed);
      if (parsedStart && !(marketStartTimes[marketId] || '').trim()) {
        payload.startTimes = { ...marketStartTimes, [marketId]: parsedStart };
      }
    }
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data) {
      if (body.data.links) setPolymarketLinks(body.data.links);
      if (body.data.startTimes) setMarketStartTimes(body.data.startTimes);
      if (body.data.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
      if (body.data.polyOutcomes) setPolyOutcomes(body.data.polyOutcomes);
      try {
        const orderResult = await fetchOrders(address.trim());
        setOrders(orderResult?.list ?? []);
        setOrdersMarkets(orderResult?.markets ?? {});
        setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
      } catch (_) {}
    }
  }, [polymarketLinks, marketStartTimes, address, fetchOrders, headers]);

  const deletePolymarketLink = useCallback(async (marketId) => {
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deleteMarketId: marketId }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.links) setPolymarketLinks(body.data.links);
    if (body?.success && body?.data?.startTimes) setMarketStartTimes(body.data.startTimes);
    if (body?.success && body?.data?.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
    if (body?.success && body?.data?.polyOutcomes) setPolyOutcomes(body.data.polyOutcomes);
  }, []);

  const saveStartTime = useCallback(async (marketId, startTimeIso) => {
    const trimmed = typeof startTimeIso === 'string' ? startTimeIso.trim() : '';
    const next = { ...marketStartTimes };
    if (trimmed) next[marketId] = trimmed;
    else delete next[marketId];
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startTimes: next }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.startTimes) setMarketStartTimes(body.data.startTimes);
    if (body?.success && body?.data?.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
  }, [marketStartTimes]);

  const toggleCancelBeforeStart = useCallback(async (marketId, enabled) => {
    const next = { ...cancelBeforeStartByMarket };
    next[marketId] = !!enabled;
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cancelBeforeStartByMarket: next }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
  }, [cancelBeforeStartByMarket]);

  const savePolyOutcome = useCallback(async (marketId, value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    const next = { ...polyOutcomes };
    if (trimmed) next[marketId] = trimmed;
    else delete next[marketId];
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ polyOutcomes: next }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.polyOutcomes) setPolyOutcomes(body.data.polyOutcomes);
    if (body?.success && address.trim()) {
      try {
        const orderResult = await fetchOrders(address.trim());
        setOrders(orderResult?.list ?? []);
        setOrdersMarkets(orderResult?.markets ?? {});
        setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
      } catch (_) {}
    }
  }, [polyOutcomes, address, fetchOrders]);

  const replaceAtBest = useCallback(
    async (params) => {
      const addr = address.trim();
      if (!addr || !apiKey.trim() || replaceOrderId) return;
      setReplaceOrderId(params.orderId);
      setOrdersError(null);
      try {
        let reqHeaders = { ...headers(), 'Content-Type': 'application/json' };
        if (API_BASE) {
          const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
          if (accountWithKey?.privateKey) {
            const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
            if (jwtResult?.token) reqHeaders = { ...reqHeaders, Authorization: `Bearer ${jwtResult.token}` };
          }
        }
        const resp = await fetch(api('/api/orders/replace-at-best'), {
          method: 'POST',
          headers: reqHeaders,
          body: JSON.stringify({
            address: addr,
            orderId: params.orderId,
            marketId: params.marketId,
            tokenId: params.tokenId,
            side: params.side,
            sharesWei: params.sharesWei,
          }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(body?.message || `操作失敗 ${resp.status}`);
        const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr);
        setOrders(list);
        setOrdersMarkets(mkt);
        setOrdersPolyOrderbook(po ?? {});
        setOrdersError(null);
      } catch (e) {
        const msg = e?.message || '一鍵撤單失敗';
        // 若後端回傳「目前無有效買一／賣一價格」，代表撤單已成功，但暫時無法自動掛到買一／賣一
        if (msg.includes('目前無有效買一／賣一價格')) {
          setOrdersError('已撤銷原掛單，但目前無有效買一／賣一價格，暫時無法自動掛單，請稍後再試。');
        } else {
          setOrdersError(msg);
        }
        // 無論是否掛單成功，都重新同步一次掛單列表，避免前端狀態與後端不一致（例如只撤單未掛單）
        try {
          const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr);
          setOrders(list);
          setOrdersMarkets(mkt);
          setOrdersPolyOrderbook(po ?? {});
        } catch (_) {
          // 若同步失敗，僅保留錯誤提示
        }
      } finally {
        setReplaceOrderId(null);
      }
    },
    [address, apiKey, headers, fetchOrders, replaceOrderId, API_BASE, savedAccounts, getJwtForOrders]
  );

  const removeOrder = useCallback(
    async (item) => {
      const addr = address.trim();
      const id = item?.id;
      if (!addr || !apiKey.trim() || !id || removingOrderId) return;
      setRemovingOrderId(id);
      setOrdersError(null);
      try {
        // 撤單需 JWT（Cloudflare 部署時前端用私鑰簽名取得）
        let orderHeaders = { ...headers() };
        const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
        if (API_BASE && accountWithKey?.privateKey) {
          const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
          if (jwtResult?.token) orderHeaders = { ...orderHeaders, Authorization: `Bearer ${jwtResult.token}` };
        }
        const resp = await fetch(api('/api/orders/remove'), {
          method: 'POST',
          headers: orderHeaders,
          body: JSON.stringify({ address: addr, ids: [id] }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(body?.message || `撤單失敗 ${resp.status}`);
        const jwtForRefetch = orderHeaders.Authorization?.replace(/^Bearer\s+/i, '') || null;
        const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr, jwtForRefetch);
        setOrders(list);
        setOrdersMarkets(mkt);
        setOrdersPolyOrderbook(po ?? {});
      } catch (e) {
        setOrdersError(e.message || '撤單失敗');
      } finally {
        setRemovingOrderId(null);
      }
    },
    [address, apiKey, headers, savedAccounts, fetchOrders, getJwtForOrders]
  );

  // API Key、帳戶或當前地址變更時同步到後端（保留 polymarketLinks）
  useEffect(() => {
    if (!configLoaded || !remember) return;
    syncConfig({
      apiKey,
      accounts: savedAccounts,
      currentAddress: address,
      polyAutoCancel,
    });
  }, [configLoaded, remember, apiKey, savedAccounts, address, polyAutoCancel, syncConfig]);

  // 本地備份（後端不可用時）
  useEffect(() => {
    if (!remember) return;
    try {
      if (apiKey) localStorage.setItem(STORAGE_API_KEY, apiKey);
      else localStorage.removeItem(STORAGE_API_KEY);
      if (address) localStorage.setItem(STORAGE_ADDRESS, address);
      else localStorage.removeItem(STORAGE_ADDRESS);
      localStorage.setItem(STORAGE_ACCOUNTS, JSON.stringify(savedAccounts));
    } catch (_) {}
  }, [remember, apiKey, address, savedAccounts]);

  // 帳戶管理操作（useEffect 會自動 sync 到後端）
  const handleAddAccount = useCallback((newAcc) => {
    const exists = savedAccounts.some(
      (a) => a.address.toLowerCase() === newAcc.address.toLowerCase()
    );
    if (exists) return;
    setSavedAccounts((prev) => [
      ...prev,
      { id: generateId(), address: newAcc.address, note: newAcc.note || '', telegramEnabled: newAcc.telegramEnabled !== false },
    ]);
  }, [savedAccounts]);

  const handleUpdateAccount = useCallback((id, updates) => {
    setSavedAccounts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a))
    );
  }, []);

  const loadQuery = useCallback(async (maybeAddrOverride) => {
    const addrOverride =
      typeof maybeAddrOverride === 'string' ? maybeAddrOverride : null;
    if (addrOverride != null && addrOverride !== '') setAddress(addrOverride);
    const key = apiKey.trim();
    const addr =
      addrOverride != null
        ? String(addrOverride).trim()
        : String(address || '').trim();
    if (!key || !addr) {
      setError('請填寫 API Key 與存款帳戶地址');
      return;
    }
    setLoading(true);
    setError(null);
    setOrdersError(null);
    setHasQueried(true);
    const LOAD_TIMEOUT_MS = 25000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('請求逾時，請檢查網路或 API Key')), LOAD_TIMEOUT_MS)
    );
    try {
      const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
      let jwt = null;
      if (API_BASE) {
        if (!accountWithKey?.privateKey) {
          setOrdersError('此帳戶尚未填寫 Privy 私鑰，無法在 Cloudflare 部署下查挂单。請在「已保存帳戶」中點該帳戶的編輯，填寫 Privy 私鑰（Predict → Account → Settings → Export）後儲存，再重新查詢。');
        } else {
          const jwtResult = await getJwtForOrders(key, addr, accountWithKey.privateKey);
          if (jwtResult?.error) {
            setOrdersError(jwtResult.error);
          } else {
            jwt = jwtResult?.token || null;
          }
        }
      }
      const [posResult, orderResult] = await Promise.race([
        Promise.all([
          fetchPositions(null, addr),
          fetchOrders(addr, jwt).catch((e) => {
            if (API_BASE && !jwt) {
              // 已在上面顯示「未填私鑰」或 JWT 取得失敗，不覆蓋
            } else {
              setOrdersError(e.message || '掛單載入失敗');
            }
            return { list: [], markets: {} };
          }),
        ]),
        timeoutPromise,
      ]);
      setAccount({ address: addr });
      setPositions(posResult.list);
      setCursor(posResult.nextCursor);
      setOrders(orderResult?.list ?? []);
      setOrdersMarkets(orderResult?.markets ?? {});
      setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
    } catch (e) {
      setError(e.message || '載入失敗');
      setAccount(null);
      setPositions([]);
      setCursor(null);
      setOrders([]);
      setOrdersMarkets({});
      setOrdersPolyOrderbook({});
    } finally {
      setLoading(false);
    }
  }, [apiKey, address, savedAccounts, fetchPositions, fetchOrders, getJwtForOrders]);

  const handleDeleteAccount = useCallback((id) => {
    setSavedAccounts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleSelectAccount = useCallback((acc) => {
    setAddress(acc.address);
    if (hasQueried && apiKey.trim() && acc?.address) loadQuery(acc.address);
  }, [hasQueried, apiKey, loadQuery]);

  const refetchData = useCallback(async () => {
    const addr = address.trim();
    if (!addr || !apiKey.trim()) return;
    const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
    let jwt = null;
    if (API_BASE && accountWithKey?.privateKey) {
      const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
      jwt = jwtResult?.token || null;
    }
    try {
      const [posResult, orderResult] = await Promise.all([
        fetchPositions(null, addr),
        fetchOrders(addr, jwt).catch(() => ({ list: [], markets: {} })),
      ]);
      setAccount({ address: addr });
      setPositions(posResult.list);
      setCursor(posResult.nextCursor);
      setOrders(orderResult?.list ?? []);
      setOrdersMarkets(orderResult?.markets ?? {});
      setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
    } catch (_) {}
  }, [address, apiKey, savedAccounts, fetchPositions, fetchOrders, getJwtForOrders]);

  // 切換帳戶時自動載入（防抖 200ms，避免連點導致多次請求）
  useEffect(() => {
    if (!hasQueried || !apiKey.trim() || !address.trim()) return;
    const tid = setTimeout(() => loadQuery(), 200);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 僅在 address 變更時觸發
  }, [address]);

  useEffect(() => {
    if (!hasQueried || pollIntervalSec <= 0 || loading) return;
    const id = setInterval(refetchData, pollIntervalSec * 1000);
    return () => clearInterval(id);
  }, [hasQueried, pollIntervalSec, refetchData, loading]);

  // 掛單頁：依當前掛單市場與 outcome 請求 Polymarket 買一賣一（體育等多選項需依 outcome 如 Nuggets→DEN）
  const orderItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    orders.forEach((o) => {
      const id = o.marketId;
      if (!id) return;
      const m = ordersMarkets[id] || {};
      const question = m.question || m.title || '';
      if (!question) return;
      const outcomeName = getOutcomeName(o.order?.tokenId, m.outcomes);
      const outcome = (outcomeName || '').trim();
      const key = `${id}::${question}::${outcome}`;
      if (seen.has(key)) return;
      seen.add(key);
      items.push({ marketId: id, question, outcome: outcome || undefined });
    });
    return items;
  }, [orders, ordersMarkets]);

  const orderItemsKey = orderItems.map((i) => `${i.marketId}:${i.question}:${i.outcome || ''}`).sort().join('|');
  const lastPolyFetchRef = React.useRef(0);
  const prevPolyOverrideRef = React.useRef('');
  const polyPollIntervalMs = Math.max(5000, polyPollIntervalSec * 1000);
  const polyFetchMinIntervalMs = Math.max(4000, polyPollIntervalMs * 0.9);
  const [polyPollTick, setPolyPollTick] = React.useState(0);

  useEffect(() => {
    if (activeTab !== 'orders' || orderItems.length === 0) return;
    const id = setInterval(() => setPolyPollTick((t) => t + 1), polyPollIntervalMs);
    return () => clearInterval(id);
  }, [activeTab, orderItems.length, polyPollIntervalMs]);

  useEffect(() => {
    if (activeTab !== 'orders' || orderItems.length === 0) {
      setPolymarketByQuestion({});
      setPolyOptionsFromApi({});
      return;
    }
    const overridesStr = JSON.stringify(polyOutcomeOverrides || {});
    if (prevPolyOverrideRef.current !== overridesStr) {
      prevPolyOverrideRef.current = overridesStr;
      lastPolyFetchRef.current = 0;
    }
    const now = Date.now();
    const hasData = Object.keys(polymarketByQuestion || {}).length > 0;
    const skipFreqRefresh = hasData && (now - lastPolyFetchRef.current < polyFetchMinIntervalMs) && Object.keys(polyOutcomeOverrides || {}).length === 0;
    let cancelled = false;
    if (!skipFreqRefresh) setPolymarketLoading(true);
    setPolymarketError(null);
    const tid = setTimeout(() => {
      fetch(api('/api/polymarket/by-questions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(apiKey.trim() ? { 'x-api-key': apiKey.trim() } : {}) },
        body: JSON.stringify({
          items: orderItems,
          ...(Object.keys(polyOutcomeOverrides).length > 0 ? { polyOutcomeOverrides } : {}),
        }),
      })
        .then(async (r) => {
          const body = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(body?.message || `請求失敗 ${r.status}`);
          return body;
        })
        .then((body) => {
          if (cancelled) return;
          lastPolyFetchRef.current = Date.now();
          if (body?.success && body?.data && typeof body.data === 'object') {
            setPolymarketByQuestion(body.data);
            if (body.polyOptions && typeof body.polyOptions === 'object') setPolyOptionsFromApi(body.polyOptions);
            setPolymarketError(null);
          } else if (!hasData) {
            setPolymarketByQuestion({});
            setPolymarketError(body?.message || '無法取得 Polymarket 數據');
          }
        })
        .catch((e) => {
          if (!cancelled && !hasData) {
            setPolymarketByQuestion({});
            setPolymarketError(e.message || 'Polymarket 請求失敗，請確認後端已啟動');
          }
        })
        .finally(() => {
          if (!cancelled) setPolymarketLoading(false);
        });
    }, hasData ? 800 : 0);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [activeTab, orderItemsKey, apiKey, JSON.stringify(polymarketLinks), JSON.stringify(polyOutcomeOverrides), polyPollTick]);

  // Cloudflare 部署時 Worker 不回傳 polyOrderbook，用 polymarketByQuestion 補齊（依 marketId 區分，僅有填寫 Poly 連結的市場才顯示）
  const effectivePolyOrderbook = useMemo(() => {
    const base = ordersPolyOrderbook && typeof ordersPolyOrderbook === 'object' ? { ...ordersPolyOrderbook } : {};
    if (!ordersMarkets || !polymarketByQuestion || typeof polymarketByQuestion !== 'object') return base;
    orders.forEach((item) => {
      const marketId = item.marketId;
      if (!polymarketLinks || !(polymarketLinks[marketId] || '').trim()) return;
      const m = ordersMarkets[marketId];
      const question = (m && (m.question || m.title)) || '';
      const outcomeName = getOutcomeName(item.order?.tokenId, m?.outcomes) || '';
      if (!question) return;
      const key = outcomeName ? `${marketId}::${outcomeName}` : marketId;
      if (base[key]?.bids?.length || base[marketId]?.bids?.length) return;
      const poly = (outcomeName && polymarketByQuestion[`${marketId}::${question}::${outcomeName}`]) || polymarketByQuestion[`${marketId}::${question}`];
      if (!poly) return;
      // 預設依該筆掛單的 outcome 決定用 Yes 還是 No 盤口；若使用者點擊 Yes/No，則以 polyViewMode 為優先
      const viewOverride = polyViewMode?.[marketId];
      const viewNo =
        viewOverride === 'no'
          ? true
          : viewOverride === 'yes'
            ? false
            : (outcomeName || '').toLowerCase() === 'no';
      const hasRawYes = (Array.isArray(poly.bidsYes) && poly.bidsYes.length > 0) || (Array.isArray(poly.asksYes) && poly.asksYes.length > 0);
      const yesBids = Array.isArray(poly.bidsYes) && poly.bidsYes.length > 0 ? poly.bidsYes : (poly.buyYes != null ? [[Number(poly.buyYes.price) || 0, Number(poly.buyYes.shares) || 0]] : poly.bids || []);
      const yesAsks = Array.isArray(poly.asksYes) && poly.asksYes.length > 0 ? poly.asksYes : (poly.sellYes != null ? [[Number(poly.sellYes.price) || 0, Number(poly.sellYes.shares) || 0]] : poly.asks || []);
      const bids = viewNo && hasRawYes ? yesAsks.map(([p, s]) => [1 - p, s]).sort((a, b) => (b[0] || 0) - (a[0] || 0)) : yesBids;
      const asks = viewNo && hasRawYes ? yesBids.map(([p, s]) => [1 - p, s]).sort((a, b) => (a[0] || 0) - (b[0] || 0)) : yesAsks;
      if (bids.length || asks.length) {
        const target = outcomeName ? key : marketId;
        base[target] = { ...(base[target] || {}), bids: bids.length ? bids : (base[target]?.bids || []), asks: asks.length ? asks : (base[target]?.asks || []) };
      }
    });
    return base;
  }, [ordersPolyOrderbook, ordersMarkets, polymarketByQuestion, polymarketLinks, orders, polyViewMode]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_POLL, String(pollIntervalSec));
    } catch (_) {}
  }, [pollIntervalSec]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { list, nextCursor } = await fetchPositions(cursor, address.trim());
      setPositions((prev) => [...prev, ...list]);
      setCursor(nextCursor);
    } catch (e) {
      setError(e.message || '載入更多失敗');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, address, fetchPositions]);

  return (
    <div className="positions-page">
      <div className="main-page-tabs">
        <button type="button" className={`main-page-tab ${mainPage === 'positions' ? 'active' : ''}`} onClick={() => setMainPage('positions')}>持倉查詢</button>
        <button type="button" className={`main-page-tab ${mainPage === 'scoreGrinding' ? 'active' : ''}`} onClick={() => setMainPage('scoreGrinding')}>全自動刷分神奇</button>
      </div>
      {mainPage === 'scoreGrinding' ? (
        <ScoreGrindingPage />
      ) : (
        <>
      <header className="positions-header">
        <h1>Predict 我的持倉</h1>
        <p className="positions-subtitle">僅支援存款帳戶查詢，不需私鑰</p>
      </header>

      <section className="positions-query-card">
        <h2 className="positions-section-title">帳戶查詢</h2>
        <p className="positions-query-desc">
          填寫 Predict API Key 與存款帳戶地址（Smart Wallet），查詢持倉與掛單。
        </p>
        <div className="positions-form">
          <div className="positions-form-row">
            <label className="positions-label">API Key</label>
            <input
              type="password"
              className="positions-input"
              placeholder="請輸入 Predict API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="positions-form-row">
            <label className="positions-label">存款帳戶地址</label>
            <input
              type="text"
              className="positions-input positions-input-address"
              placeholder="0x..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="positions-form-row positions-form-actions">
            <label className="positions-checkbox-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              記住 API Key 與帳戶（同步後端，刷新後保留）
            </label>
            <button
              type="button"
              className="positions-query-btn"
              onClick={() => loadQuery()}
              disabled={loading || !apiKey.trim() || !address.trim()}
            >
              {loading ? '查詢中…' : '查詢持倉'}
            </button>
          </div>
          {hasQueried && (
            <>
              <div className="positions-form-row positions-poll-row">
                <label className="positions-label">輪詢間隔（持倉／掛單自動更新）</label>
                <select
                  className="positions-poll-select"
                  value={pollIntervalSec}
                  onChange={(e) => setPollIntervalSec(Number(e.target.value))}
                >
                  {POLL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="positions-form-row positions-poll-row">
                <label className="positions-label">Poly 市場輪詢間隔（買一／賣一價格，至少 5 秒）</label>
                <select
                  className="positions-poll-select"
                  value={polyPollIntervalSec}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPolyPollIntervalSec(v);
                    try { localStorage.setItem(STORAGE_POLY_POLL, String(v)); } catch (_) {}
                  }}
                >
                  {POLY_POLL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </section>

      {savedAccounts.length > 0 && (
        <section className="positions-query-card positions-current-account-bar">
          <h2 className="positions-section-title">當前查看帳戶</h2>
          <p className="positions-query-desc">點擊切換帳戶，若已查詢過會自動刷新該帳戶持倉與掛單。</p>
          <div className="account-tabs-row">
            {savedAccounts.map((acc) => {
              const isActive = (acc.address || '').toLowerCase() === (address || '').toLowerCase();
              const label = acc.note?.trim() || `${(acc.address || '').slice(0, 6)}…${(acc.address || '').slice(-4)}`;
              return (
                <button
                  key={acc.id}
                  type="button"
                  className={`account-tab-btn ${isActive ? 'active' : ''}`}
                  onClick={() => handleSelectAccount(acc)}
                  title={acc.address}
                >
                  {acc.privateKey && <span className="account-tab-key-icon" title="已設私鑰">🔑</span>}
                  <span className="account-tab-label">{label}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="positions-query-card">
        <h2 className="positions-section-title">機器人配置</h2>
        <p className="positions-query-desc">
          配置訂單提醒推送。填寫 Webhook 後點擊「同步到後端」保存。
        </p>
        <div className="positions-form">
          <div className="positions-form-row">
            <label className="positions-label">企業微信 Webhook URL</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <input
                type="text"
                className="positions-input"
                placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=xxxxx"
                value={wecomWebhookUrl}
                onChange={(e) => setWecomWebhookUrl(e.target.value)}
                autoComplete="off"
              />
              <span className="positions-query-desc">
                企業微信群組 → 新增群機器人 → 複製 webhook 地址
              </span>
            </div>
          </div>
          <div className="positions-form-row">
            <span className="positions-label">提醒類型</span>
            <div className="positions-telegram-alerts" style={{ flexWrap: 'wrap', gap: '8px 16px' }}>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderPlaced}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderPlaced: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                掛單提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderCancelled}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderCancelled: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                撤單提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderFilled}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderFilled: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                成交提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderDanger}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderDanger: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                危險狀態提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.notAtBest}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, notAtBest: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                買一賣一提醒
              </label>
            </div>
          </div>
          <div className="positions-form-row">
            <label className="positions-label">輪詢間隔（秒）</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                className="positions-input"
                style={{ maxWidth: 120 }}
                min={10}
                max={3600}
                value={telegramPollSec}
                onChange={(e) => {
                  const v = Number(e.target.value) || 10;
                  const clamped = Math.max(10, Math.min(3600, v));
                  setTelegramPollSec(clamped);
                  syncConfig({ telegramPollIntervalSec: clamped });
                }}
              />
              <span className="positions-query-desc">10～3600，越小越即時</span>
            </div>
          </div>
          {API_BASE && (
            <div className="positions-form-row">
              <label className="positions-label">10 秒輪詢觸發 URL</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <code style={{ fontSize: 12, wordBreak: 'break-all', background: 'rgba(30,41,59,0.8)', padding: 8, borderRadius: 8 }}>
                  {api('/api/cron/order-check')}
                </code>
                <span className="positions-query-desc">
                  雲端 Cron 最小 1 分鐘。若要 10 秒輪詢，請用外部定時服務（如 cron-job.org、自建腳本）每 10 秒 GET 此 URL。若設了 wrangler secret CRON_TRIGGER_SECRET，需帶 X-Cron-Secret 或 ?secret=xxx
                </span>
              </div>
            </div>
          )}
          <div className="positions-form-row positions-form-actions" style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="positions-query-btn"
              onClick={() => syncConfig({
                wecomWebhookUrl: wecomWebhookUrl.trim(),
                telegramAlerts,
                telegramPollIntervalSec: telegramPollSec,
              })}
            >
              同步到後端
            </button>
            <TestBotButton />
          </div>
        </div>
      </section>

      {error && (
        <div className="positions-error">
          {error}
          <button type="button" className="positions-retry-btn" onClick={() => loadQuery()}>
            重試
          </button>
        </div>
      )}

      {(account || configLoaded) && (
        <section className="positions-account-card">
          <h2 className="positions-section-title">帳戶資訊</h2>
          {account && hasQueried && !loading && (
            <div className="positions-account-row">
              <div className="positions-account-info">
                <div className="positions-account-name">
                  存款帳戶
                  {currentAccountNote && (
                    <span className="positions-account-note-badge">{currentAccountNote}</span>
                  )}
                </div>
                <div className="positions-account-address" title={account.address}>
                  {account.address
                    ? `${account.address.slice(0, 6)}…${account.address.slice(-4)}`
                    : '—'}
                </div>
              </div>
            </div>
          )}
          <AccountManager
            accounts={savedAccounts}
            currentAddress={address}
            onSelect={handleSelectAccount}
            onAdd={handleAddAccount}
            onUpdate={handleUpdateAccount}
            onDelete={handleDeleteAccount}
          />
        </section>
      )}

      {hasQueried && (
        <section className={`positions-list-section ${loading ? 'positions-list-loading' : ''}`}>
          {loading && (
            <div className="positions-loading-overlay">
              <span>載入中…</span>
            </div>
          )}
          <div className="positions-list-current-account">
            當前帳戶：<strong>{currentAccountNote || (address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '—')}</strong>
          </div>
          <div className="positions-tabs">
            <button
              type="button"
              className={`positions-tab ${activeTab === 'positions' ? 'active' : ''}`}
              onClick={() => setActiveTab('positions')}
            >
              Positions ({positions.length})
            </button>
            <button
              type="button"
              className={`positions-tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Open Orders ({orders.length})
            </button>
            <button
              type="button"
              className={`positions-tab ${activeTab === 'auto' ? 'active' : ''}`}
              onClick={() => setActiveTab('auto')}
            >
              全自動掛單撤單
            </button>
          </div>

          {activeTab === 'positions' && (
            <>
              <h2 className="positions-section-title">
                持倉列表 {positions.length > 0 && `(${positions.length})`}
              </h2>
              {positions.length === 0 && !error ? (
            <div className="positions-empty">
              目前無持倉，或請確認 API Key、地址是否正確
            </div>
          ) : (
            <>
              <div className="positions-cards-header">
                <span className="positions-ch-outcome">Outcome</span>
                <span className="positions-ch-shares">Shares</span>
                <span className="positions-ch-value">Value</span>
                <span className="positions-ch-towin">To Win</span>
                <span className="positions-ch-action" />
              </div>
              <ul className="positions-cards-list">
                {positions.map((pos) => {
                  const market = pos.market || {};
                  const outcome = pos.outcome || {};
                  const question =
                    market.question || market.title || `市場 ${market.id ?? '—'}`;
                  const outcomeName =
                    outcome.name ??
                    (outcome.indexSet === 1
                      ? 'Yes'
                      : outcome.indexSet === 2
                        ? 'No'
                        : '—');
                  const isYes = outcomeName.toLowerCase() === 'yes';
                  const amountNum =
                    pos.amount != null ? Number(pos.amount) : null;
                  const valueUsd =
                    pos.valueUsd != null ? Number(pos.valueUsd) : null;
                  const shares =
                    amountNum != null && !Number.isNaN(amountNum)
                      ? amountNum >= 1e10
                        ? (amountNum / 1e18).toFixed(2)
                        : amountNum.toFixed(2)
                      : '—';
                  const valueStr =
                    valueUsd != null && !Number.isNaN(valueUsd)
                      ? `$${valueUsd.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '—';
                  const toWinNum =
                    amountNum != null && !Number.isNaN(amountNum)
                      ? amountNum >= 1e10
                        ? amountNum / 1e18
                        : amountNum
                      : null;
                  const toWinStr =
                    toWinNum != null
                      ? `$${Number(toWinNum).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '—';
                  const marketSlug = market.categorySlug || market.slug || market.id;
                  const sellLink =
                    market.id != null
                      ? `https://predict.fun/market/${marketSlug || market.id}`
                      : 'https://predict.fun';
                  return (
                    <li
                      key={
                        pos.id ||
                        `${market.id}-${outcome.onChainId || outcome.indexSet}`
                      }
                      className="position-card"
                    >
                      <div className="position-card-market">
                        <div className="position-card-icon">
                          {market.imageUrl ? (
                            <img
                              src={market.imageUrl}
                              alt=""
                              className="position-card-icon-img"
                            />
                          ) : (
                            <span className="position-card-icon-placeholder">
                              {question.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <h3 className="position-card-question" title={question}>
                          {question}
                        </h3>
                      </div>
                      <div className="position-card-details">
                        <span
                          className={`position-outcome ${isYes ? 'outcome-yes' : 'outcome-no'}`}
                        >
                          {outcomeName}
                        </span>
                        <span className="position-shares">{shares}</span>
                        <span className="position-value">{valueStr}</span>
                        <span className="position-towin">{toWinStr}</span>
                        <a
                          href={sellLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="position-sell-btn"
                        >
                          Sell ({valueStr})
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {cursor && (
            <div className="positions-load-more">
              <button
                type="button"
                className="positions-load-more-btn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? '載入中…' : '載入更多'}
              </button>
            </div>
          )}
            </>
          )}

          {activeTab === 'orders' && (
            <>
              <h2 className="positions-section-title">
                掛單列表 {orders.length > 0 && `(${orders.length})`}
              </h2>
              <div className="positions-form-row" style={{ marginBottom: 12 }}>
                <span className="positions-label">Polymarket 風險自動撤單</span>
                <div className="positions-telegram-alerts">
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.danger}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, danger: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當狀態為「危險」時，立即撤掉該市場所有掛單
                  </label>
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.balancedLowBook}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, balancedLowBook: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當狀態為「均衡」且 Poly 買一 shares &lt; 門檻時自動撤單
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="positions-label">門檻（shares）</span>
                    <input
                      type="number"
                      className="positions-input"
                      style={{ maxWidth: 140 }}
                      min={100}
                      max={1000000}
                      value={polyAutoCancel.balancedLowBookMinShares}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        const clamped = Math.max(100, Math.min(1000000, v));
                        const next = { ...polyAutoCancel, balancedLowBookMinShares: clamped };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                  </div>
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.cancelOnPartialFill}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, cancelOnPartialFill: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當訂單有部分成交時，立即撤掉該筆剩餘掛單（例：掛 1000 成交 1 → 撤掉剩餘 999）
                  </label>
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.buyOneBelowOrder2x}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, buyOneBelowOrder2x: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當 Polymarket 買一 shares 不足我掛單 shares 的 2 倍時，自動撤該筆單（以你看到的 Poly 盤口為準，例：掛 2000，買一剩 3900 時撤單）
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label className="positions-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ whiteSpace: 'nowrap' }}>當單筆訂單成交 shares 超過</span>
                      <input
                        type="number"
                        className="positions-input"
                        style={{ width: 72 }}
                        min={0}
                        max={1000000}
                        value={polyAutoCancel.cancelAllOnPartialFillOver ?? 0}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(1000000, Math.floor(Number(e.target.value) || 0)));
                          const next = { ...polyAutoCancel, cancelAllOnPartialFillOver: v };
                          setPolyAutoCancel(next);
                          if (remember) syncConfig({ polyAutoCancel: next });
                        }}
                      />
                      <span>時，撤銷該市場全部訂單（0＝關閉）</span>
                    </label>
                  <label className="positions-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ whiteSpace: 'nowrap' }}>當 Poly 買一價格（¢）+ 我掛單價格（¢）&gt;</span>
                    <input
                      type="number"
                      className="positions-input"
                      style={{ width: 72 }}
                      min={0}
                      max={200}
                      value={polyAutoCancel.priceSumThresholdCents ?? 0}
                      onChange={(e) => {
                        const v = Math.max(0, Math.min(200, Math.floor(Number(e.target.value) || 0)));
                        const next = { ...polyAutoCancel, priceSumThresholdCents: v };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    <span>時，自動撤掉該筆 Buy 單（0＝關閉）</span>
                  </label>
                  {isPredict2Site && (
                  <div className="positions-telegram-item" style={{ marginTop: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span>價差閾值</span>
                      <input
                        type="number"
                        className="positions-input"
                        style={{ width: 72 }}
                        min={0}
                        max={50}
                        value={polyAutoCancel.priceSpreadThresholdCents ?? 0}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(50, Math.floor(Number(e.target.value) || 0)));
                          const next = { ...polyAutoCancel, priceSpreadThresholdCents: v };
                          setPolyAutoCancel(next);
                          if (remember) syncConfig({ polyAutoCancel: next });
                        }}
                      />
                      <span>¢</span>
                    </label>
                    <span className="positions-query-desc" style={{ display: 'block', marginTop: 4 }}>價差 ≤ 此值不撤單，&gt; 才撤單；0＝嚴格</span>
                  </div>
                  )}
                  </div>
                </div>
              </div>
              {ordersError && (
                <div className="positions-orders-error">
                  {ordersError}
                  <span className="orders-error-hint">
                    （若為 Cloudflare 部署：挂单需 JWT，請改用本地後端並在帳戶填寫 Privy 私鑰；
                    若為本地後端且顯示「需要 JWT」，請在「已保存帳戶」填寫該帳戶的 Privy 私鑰；
                    若顯示「目前無有效買一／賣一價格」，代表該市場暫時沒有報價，只能先撤單，稍後再試自動掛單）
                  </span>
                </div>
              )}
              {orders.length === 0 && !ordersError ? (
                <div className="positions-empty">
                  目前無掛單，或請確認 API 是否支援取得掛單
                </div>
              ) : (
                <>
                  <OpenOrdersList
                    orders={orders}
                    markets={ordersMarkets}
                    polymarketByQuestion={polymarketByQuestion}
                    polymarketLoading={polymarketLoading}
                    polymarketLinks={polymarketLinks}
                    polyOrderbookByMarket={effectivePolyOrderbook}
                    marketStartTimes={marketStartTimes}
                    cancelBeforeStartByMarket={cancelBeforeStartByMarket}
                    polyOutcomes={polyOutcomes}
                    polyOutcomeOverrides={polyOutcomeOverrides}
                    onSetPolyOverride={(marketId, opt) => {
                      setPolyOutcomeOverrides((prev) => {
                        const next = { ...prev };
                        if (opt) next[marketId] = opt; else delete next[marketId];
                        return next;
                      });
                      // 點擊「強制顯示」時，同步保存到後端，刷新或換設備也能記住
                      const value = typeof opt === 'string' ? opt.trim() : '';
                      savePolyOutcome(marketId, value);
                    }}
                    polyOptionsFromApi={polyOptionsFromApi}
                    polyViewMode={polyViewMode}
                    onSetPolyViewMode={(marketId, mode) => setPolyViewMode((prev) => {
                      const next = { ...prev };
                      if (mode) next[marketId] = mode; else delete next[marketId];
                      return next;
                    })}
                    onSavePolyLink={savePolymarketLink}
                    onDeletePolyLink={deletePolymarketLink}
                    onSaveStartTime={saveStartTime}
                    onToggleCancelBeforeStart={toggleCancelBeforeStart}
                    onSavePolyOutcome={savePolyOutcome}
                    onReplaceAtBest={replaceAtBest}
                    replaceOrderId={replaceOrderId}
                    onRemoveOrder={removeOrder}
                    removingOrderId={removingOrderId}
                    onAddToAutoTrading={addOrderToAutoTrading}
                    priceRuleEnabledByMarket={priceRuleEnabledByMarket}
                    onTogglePriceRule={(marketId, enabled) => {
                      setPriceRuleEnabledByMarket((prev) => ({ ...prev, [marketId]: enabled }));
                    }}
                    polyAutoOptions={{
                      autoCancelDanger: !!polyAutoCancel.danger,
                      autoCancelBalancedLowBook: !!polyAutoCancel.balancedLowBook,
                      balancedLowBookMinShares: polyAutoCancel.balancedLowBookMinShares,
                      autoCancelOnPartialFill: !!polyAutoCancel.cancelOnPartialFill,
                      autoCancelBuyOneBelowOrder2x: !!polyAutoCancel.buyOneBelowOrder2x,
                      cancelAllOnPartialFillOver: Math.max(0, Math.floor(Number(polyAutoCancel.cancelAllOnPartialFillOver) || 0)),
                      priceSumThresholdCents: Math.max(0, Math.min(200, Math.floor(Number(polyAutoCancel.priceSumThresholdCents) || 0))),
                      priceSpreadThresholdCents: Math.max(0, Math.min(50, Math.floor(Number(polyAutoCancel.priceSpreadThresholdCents) || 0))),
                    }}
                  />
                  {polymarketError && (
                    <div className="polymarket-inline-error">{polymarketError}</div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'auto' && (
            <>
              <h2 className="positions-section-title">全自動掛單撤單測試</h2>
              <p className="positions-query-desc" style={{ marginBottom: 16 }}>
                輪詢 1～3 秒：買一 shares ≥ 1000 且掛單在買一則保持；&lt; 1000 立刻撤單；不在買一時若買一 ≥ 1000 則掛單。股數 min 100、max 市場買一 30%。1 分鐘無法讀取則撤銷該市場所有掛單。
              </p>
              <div className="positions-form-row">
                <label className="positions-label">啟用全自動</label>
                <input
                  type="checkbox"
                  checked={autoTrading.enabled}
                  onChange={(e) => patchAutoTrading({ enabled: e.target.checked })}
                />
              </div>
              <div className="positions-form-row">
                <label className="positions-label">輪詢間隔（秒）</label>
                <select value={autoTrading.pollIntervalSec ?? 2} onChange={(e) => patchAutoTrading({ pollIntervalSec: Number(e.target.value) })} style={{ width: 100 }}>
                  <option value={1}>1 秒</option>
                  <option value={2}>2 秒</option>
                  <option value={3}>3 秒</option>
                  <option value={5}>5 秒</option>
                  <option value={10}>10 秒</option>
                </select>
                <span className="positions-query-desc" style={{ marginLeft: 8 }}>1～3 秒建議</span>
              </div>
              <div className="positions-form-row" style={{ alignItems: 'flex-start' }}>
                <label className="positions-label">監控市場</label>
                <div>
                  {autoTrading.markets.length === 0 ? (
                    <p className="positions-query-desc">從掛單列表點「加入全自動」加入市場，或先查詢掛單後再加入</p>
                  ) : (
                    <ul className="auto-trading-list">
                      {autoTrading.markets.map((m) => {
                        const key = `${m.marketId}_${m.tokenId}`;
                        const st = autoTrading.status?.[key] || {};
                        return (
                          <li key={m.id || key} className="auto-trading-item">
                            <span>市場 {m.marketId} · {m.outcomeName} · {m.shares} 股 · {m.accountNote || m.accountAddress?.slice(0, 8)}…</span>
                            {st.blacklisted && <span className="auto-trading-badge auto-trading-blacklist">黑名單 30 分鐘</span>}
                            <button
                              type="button"
                              className="positions-retry-btn"
                              style={{ marginLeft: 8, padding: '4px 10px', fontSize: 12 }}
                              onClick={() => patchAutoTrading({ markets: autoTrading.markets.filter((x) => (x.id || `${x.marketId}_${x.tokenId}`) !== (m.id || key)) })}
                            >
                              移除
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      )}
        </>
      )}
    </div>
  );
}
